import React from 'react';
import { Icon } from '../icons.jsx';
import {
  LizziLogo, Avatar, AvatarStack, StatusChip,
  Sidebar, UserMenu, MenuItem,
  Topbar, CommandPalette,
  ChatDock, ChatMessage, FormattedText, ChatBlock,
  ToastHost,
  ProgressBar, StatPill,
  fmtMoney, fmtPct, fmtDate, fmtDateFull,
  fixMoneyText, fixMoneyKpi,
} from '../components.jsx';
import { NewBidModal, UploadModal, NotificationsPanel, EditBidModal, notify } from '../modals.jsx';
import { MockData } from '../mock/index.js';
import { fetchBid, analyzeBid, retryAnalysis, listBidLots, updateGoData, listBidDocuments, getBidDocumentUrl, refreshBidInStore, generateShortTitles } from '../data/repo.js';
import { StatusSelect } from './bids.jsx';
import { bidTitle } from '../components.jsx';
import { downloadIILWord } from '../data/exportWord.js';
import { downloadOnePagerWord, downloadOnePagerPdf } from '../data/exportOnePager.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { BudgetBuilder } from './budget-builder.jsx';

const { useState, useEffect, useRef, useMemo, useCallback } = React;

// =========================================================
// Bid detail / Análisis ficha — SANDETEL flagship
// =========================================================
function BidDetailScreen({ bidId, back, openEditor, embedded = false }) {
  const bid = window.MockData.BIDS.find(b => b.id === bidId) || window.MockData.BIDS[0];
  const isSandetel = bid && bid.id === 'sandetel-25-00158';
  const [tab, setTab] = useState('overview');
  const tabs = [
    { id:'overview',   label:'Resumen',              icon:'Layout' },
    { id:'budget',     label:'Presupuesto',          icon:'Coins' },
    { id:'criteria',   label:'Criterios',            icon:'Scale' },
    { id:'team',       label:'Equipo y dedicación',  icon:'Users' },
    { id:'ans',        label:'ANS y penalizaciones', icon:'Shield' },
    { id:'risks',      label:'Riesgos',              icon:'AlertTriangle' },
    { id:'deliverables', label:'Entregables',        icon:'Cube' },
    { id:'documents',  label:'Documentos · 14',      icon:'FileText' },
  ];

  if (!bid) {
    return (
      <div className="viewport-inner fade-in" style={{padding:60, textAlign:'center', color:'var(--text-3)'}}>
        <Icon.Briefcase size={28}/>
        <div style={{marginTop:12, fontSize:15, color:'var(--text-2)'}}>Esta licitación no existe o aún no se ha creado.</div>
        <button className="btn btn-outline" style={{marginTop:18}} onClick={back}><Icon.ArrowLeft size={14}/> Volver a licitaciones</button>
      </div>
    );
  }

  if (!isSandetel) return <BidDetailGeneric bid={bid} back={back} embedded={embedded}/>;

  return (
    <div className="fade-in" style={{display:'flex', flexDirection:'column', height:'100%'}}>
      {!embedded && <SandetelHeader bid={bid} back={back} openEditor={openEditor}/>}
      <div style={{padding:'16px 36px 0', background:'var(--bg-0)', position:'sticky', top:0, zIndex:5}}>
        <div style={{maxWidth:1320, margin:'0 auto'}}>
          <TabNav tabDefs={tabs} tab={tab} setTab={setTab}/>
        </div>
      </div>

      <div style={{flex:1, overflow:'auto'}}>
        <div style={{maxWidth:1320, margin:'0 auto', padding:'28px 36px 80px'}}>
          {tab === 'overview'    && <SandetelOverview/>}
          {tab === 'budget'      && <SandetelBudget/>}
          {tab === 'criteria'    && <SandetelCriteria/>}
          {tab === 'team'        && <SandetelTeam/>}
          {tab === 'ans'         && <SandetelANS/>}
          {tab === 'risks'       && <SandetelRisks/>}
          {tab === 'deliverables'&& <SandetelDeliverables/>}
          {tab === 'documents'   && <SandetelDocuments/>}
        </div>
      </div>
    </div>
  );
}

function SandetelHeader({ bid, back, openEditor }) {
  return (
    <div style={{padding:'24px 36px 18px', background:'var(--bg-0)', borderBottom:'1px solid var(--line-1)'}}>
      <div style={{maxWidth:1320, margin:'0 auto'}}>
        <div style={{display:'flex', alignItems:'center', gap:8, fontSize:12, color:'var(--text-3)', marginBottom:12}}>
          <button className="btn btn-ghost" onClick={back} style={{padding:'4px 8px', fontSize:12}}><Icon.ChevronLeft size={13}/> Licitaciones</button>
          <span>·</span>
          <span className="font-mono" style={{color:'var(--text-4)'}}>{bid.code}</span>
          <span>·</span>
          <span>{bid.lot}</span>
        </div>

        <div style={{display:'flex', alignItems:'flex-start', gap:24}}>
          <div style={{flex:1, minWidth:0}}>
            <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:8}}>
              <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.04em'}}>SANDETEL · Sociedad Andaluza para el Desarrollo de las Telecomunicaciones</span>
              <StatusChip status={bid.status}/>
              <span className="chip" style={{fontSize:10.5}}>{bid.region}</span>
              <span className="chip" style={{fontSize:10.5}}>{bid.cpv}</span>
            </div>
            <h1 className="page-title" style={{fontSize:34}}>{bid.title}</h1>
            <div style={{display:'flex', alignItems:'center', gap:16, marginTop:10, fontSize:13, color:'var(--text-3)'}}>
              <span style={{display:'flex',alignItems:'center',gap:6}}><Icon.Calendar size={13}/> Cierre {fmtDateFull(bid.deadline)} · <span style={{color:'var(--sem-warning)'}}>en {bid.deadlineDays} días</span></span>
              <span style={{display:'flex',alignItems:'center',gap:6}}><Icon.Users size={13}/> 4 personas</span>
              <span style={{display:'flex',alignItems:'center',gap:6}}><Icon.FileText size={13}/> {bid.documents} documentos</span>
              <span style={{display:'flex',alignItems:'center',gap:6}}><Icon.Clock size={13}/> Actualizado {bid.lastActivity.toLowerCase()}</span>
            </div>
          </div>
          <div style={{display:'flex', gap:8, flexShrink:0}}>
            <button className="btn btn-outline" onClick={()=>notify('Exportando ficha completa de SANDETEL a PDF', 'Download')}><Icon.Download size={14}/> Exportar ficha</button>
            <button className="btn btn-outline" onClick={()=>notify('Lizzie está re-analizando el pliego', 'Sparkles')}><Icon.Sparkles size={14}/> Re-analizar</button>
            <button className="btn btn-primary" onClick={openEditor}><Icon.PenTool size={14}/> Abrir memoria</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Tab: Overview ----------
function SandetelOverview() {
  const s = window.MockData.SANDETEL;
  return (
    <div className="stagger" style={{display:'flex', flexDirection:'column', gap:24}}>
      {/* Hero KPIs */}
      <div>
        <div className="section-title" style={{marginBottom:14}}>Indicadores clave del pliego</div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16}}>
          <BigKpi label="Presupuesto base" value="1,87" unit="M€" sub="sin IVA · 2,27 M€ con IVA" tone="primary"/>
          <BigKpi label="Valor estimado total" value="5,99" unit="M€" sub="con prórrogas + modificaciones"/>
          <BigKpi label="Duración inicial" value="12" unit="meses" sub="+ 2 prórrogas × 12 m."/>
          <BigKpi label="Umbral técnico mín." value="30" unit="/ 50" sub="Sobre 2 · criterios subjetivos"/>
        </div>
      </div>

      {/* Compromisos cuantificables */}
      <div>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between', marginBottom:10}}>
          <div>
            <div className="section-title">Compromisos cuantificables</div>
            <div style={{fontSize:12, color:'var(--text-3)', marginTop:2}}>Mínimos exigidos por el pliego · puntúa en Sobre 3</div>
          </div>
          <span className="chip"><Icon.Lightning size={11}/> Vinculantes en oferta</span>
        </div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14}}>
          {s.heroIndicators.map((k,i) => {
            const Ico = Icon[k.icon];
            const tones = ['sem-info','sem-warning','sem-violet','sem-success'];
            return (
              <div key={k.id} className="kpi" style={{position:'relative'}}>
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                  <span className={`sem ${tones[i]}`} style={{padding:'2px 6px'}}><Ico size={11}/></span>
                  <span className="font-mono" style={{fontSize:11, color:'var(--text-4)'}}>{k.unit}</span>
                </div>
                <div className="kpi-value" style={{marginTop:8}}>{k.value}</div>
                <div style={{fontSize:11.5, color:'var(--text-2)', marginTop:2}}>{k.label}</div>
                <div style={{fontSize:11, color:'var(--text-4)', marginTop:6}}>{k.sub}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Two-col: Objeto + Lizzi insight */}
      <div style={{display:'grid', gridTemplateColumns:'2fr 1fr', gap:16}}>
        <div className="card card-pad">
          <div className="section-title" style={{marginBottom:12}}>Objeto y alcance del contrato</div>
          <p style={{fontSize:14, lineHeight:1.6, color:'var(--text-2)', margin:0}}>
            Servicios profesionales especializados para el <strong style={{color:'var(--text-1)'}}>desarrollo del programa marco de aceleración digital</strong> de SANDETEL, dirigido a startups en estadios <em>seed</em> y <em>early-stage</em> con presencia en Andalucía.
            El contrato cubre la operación de hasta <strong style={{color:'var(--text-1)'}}>4 centros de aceleración</strong> simultáneos, con posibilidad de ampliación a 2 adicionales sujeto a aprobación del órgano contratante.
          </p>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginTop:16}}>
            {[
              ['Centros aceleración', '4', 'Ampliable a 6'],
              ['Subcontratación máx.', '40%', 'Sobre coste total'],
              ['Variantes', 'No', 'Oferta única'],
            ].map(([l,v,s],i)=> (
              <div key={i} style={{padding:12, background:'var(--bg-2)', borderRadius:8}}>
                <div style={{fontSize:11, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.05em'}}>{l}</div>
                <div className="font-display" style={{fontSize:22, marginTop:2}}>{v}</div>
                <div style={{fontSize:11, color:'var(--text-4)', marginTop:2}}>{s}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{display:'flex', flexDirection:'column', gap:12}}>
          <div className="callout callout-warn">
            <div className="callout-icon"><Icon.AlertTriangle size={16}/></div>
            <div>
              <div style={{fontWeight:500, marginBottom:4}}>Atención · escala de compromisos</div>
              <div style={{fontSize:12.5, lineHeight:1.5}}>
                Pasar de <strong>66 a 90 startups</strong> en plataforma de inversión supone <strong>+36% de captación</strong> para ganar solo <strong>4 puntos</strong>. Conviene comprometer 78 (zona dulce).
              </div>
            </div>
          </div>
          <div className="callout callout-info">
            <div className="callout-icon"><Icon.Info size={16}/></div>
            <div style={{fontSize:12.5, lineHeight:1.5}}>
              Los valores comprometidos en el Sobre 3 son <strong>vinculantes</strong> y sustentan los ANS de la fase de ejecución. Una infraejecución del 20% activa la penalización proporcional.
            </div>
          </div>
          <div className="callout callout-success">
            <div className="callout-icon"><Icon.CheckCircle size={16}/></div>
            <div style={{fontSize:12.5, lineHeight:1.5}}>
              Tarifario interno cruza con el presupuesto: <strong>holgura del 12,4%</strong>. Margen suficiente para escenario base. Lizzi ha identificado 3 escenarios de oferta.
            </div>
          </div>
        </div>
      </div>

      {/* Special conditions */}
      <div>
        <div className="section-title" style={{marginBottom:10}}>Condiciones especiales de ejecución</div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12}}>
          <ConditionCard icon="Shield" tone="sem-info" title="RGPD reforzado"
            body="Obligación esencial. Tratamiento de datos de startups participantes con base jurídica documentada y DPO designado." note="Aporta cláusula RGPD reforzada de la biblioteca."/>
          <ConditionCard icon="Layers" tone="sem-success" title="Cero papel"
            body="Compromiso ambiental obligatorio. Toda la documentación operativa en formato digital con firma electrónica certificada." note="Aporta cláusula 'cero papel' de la biblioteca."/>
          <ConditionCard icon="Users" tone="sem-violet" title="Cláusula social"
            body="Empleo de calidad del equipo · obligación de mantener jornada completa para 60% del personal asignado." note="Aporta cláusula social de empleo de calidad."/>
        </div>
      </div>
    </div>
  );
}

function BigKpi({ label, value, unit, sub, tone='neutral' }) {
  const isAccent = tone === 'primary';
  const isLime = tone === 'lime';
  return (
    <div className={`kpi ${isAccent ? 'kpi-accent' : isLime ? 'kpi-lime' : ''}`}>
      <div className="kpi-label">{label}</div>
      <div style={{display:'flex', alignItems:'baseline', gap:8, marginTop:6}}>
        <span className="font-display-heavy" style={{fontSize:48, lineHeight:1, letterSpacing:'-0.04em'}}>{value}</span>
        <span className="font-mono" style={{fontSize:14, color: isAccent ? 'rgba(255,255,255,0.7)' : 'var(--text-3)'}}>{unit}</span>
      </div>
      <div style={{fontSize:12.5, color: isAccent ? 'rgba(255,255,255,0.7)' : 'var(--text-3)', marginTop:'auto', paddingTop:10}}>{sub}</div>
    </div>
  );
}

function ConditionCard({ icon, tone, title, body, note }) {
  const Ico = Icon[icon];
  return (
    <div className="card card-pad">
      <div style={{display:'flex',alignItems:'center', gap:10, marginBottom:10}}>
        <span className={`sem ${tone}`} style={{width:28, height:28, padding:0, justifyContent:'center', borderRadius:8}}><Ico size={14}/></span>
        <span style={{fontWeight:500, fontSize:14}}>{title}</span>
      </div>
      <p style={{fontSize:13, color:'var(--text-2)', lineHeight:1.55, margin:0}}>{body}</p>
      {note && <div style={{marginTop:10, fontSize:11.5, color:'var(--text-4)', display:'flex', alignItems:'center', gap:6}}><Icon.Link size={11}/> {note}</div>}
    </div>
  );
}

// ---------- Tab: Budget ----------
function SandetelBudget() {
  const s = window.MockData.SANDETEL;
  return (
    <div style={{display:'flex', flexDirection:'column', gap:24}}>
      <div>
        <div className="section-title" style={{marginBottom:10}}>Estructura del presupuesto</div>
        <div style={{display:'grid', gridTemplateColumns:'1.6fr 1fr', gap:18}}>
          <div className="card" style={{overflow:'hidden'}}>
            <table className="tbl">
              <thead><tr><th>Concepto</th><th style={{textAlign:'right'}}>Importe (€)</th><th style={{textAlign:'right'}}>% sobre valor total</th></tr></thead>
              <tbody>
                {s.budgetBreakdown.map((r,i)=> (
                  <tr key={i}>
                    <td>
                      <div style={{display:'flex', alignItems:'center', gap:8}}>
                        <span style={{width:8, height:8, borderRadius:2, background: r.tone==='warning'?'var(--sem-warning)':r.tone==='info'?'var(--sem-info)':'var(--text-3)'}}></span>
                        {r.concept}
                      </div>
                    </td>
                    <td style={{textAlign:'right', fontFamily:'JetBrains Mono'}}>{r.amount}</td>
                    <td style={{textAlign:'right', fontFamily:'JetBrains Mono', color:'var(--text-3)'}}>{r.pct.toFixed(1).replace('.', ',')}%</td>
                  </tr>
                ))}
                <tr style={{background:'var(--bg-2)', borderTop:'2px solid var(--line-2)'}}>
                  <td style={{fontWeight:500}}>Valor estimado total (sin IVA)</td>
                  <td style={{textAlign:'right', fontFamily:'JetBrains Mono', fontWeight:500}}>5.990.400,00</td>
                  <td style={{textAlign:'right', fontFamily:'JetBrains Mono', color:'var(--text-3)'}}>100,0%</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="card card-pad" style={{background:'linear-gradient(160deg, var(--bg-1), var(--bg-2))'}}>
            <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:14}}>
              <Icon.Calculator size={15}/>
              <span style={{fontSize:13, fontWeight:500}}>Cruce con el tarifario interno</span>
            </div>
            <div style={{display:'flex', flexDirection:'column', gap:14}}>
              <BudgetRow label="Coste objetivo estimado" value="1,64 M€" sub="Personal: 1,18 M€ · Externos: 460 k€" tone="neutral"/>
              <BudgetRow label="Holgura sobre base" value="+12,4%" sub="231.500 € margen bruto" tone="success"/>
              <BudgetRow label="Riesgo baja anómala" value="Bajo" sub="Umbral: −15% vs media estimada" tone="success"/>
              <BudgetRow label="Costes ocultos detectados" value="46.300 €" sub="PRL · plataforma · auditoría externa" tone="warning"/>
            </div>
            <button className="btn btn-outline" style={{width:'100%', marginTop:18, justifyContent:'center'}} onClick={()=>notify('Abriendo simulador de escenarios de oferta', 'Wand')}><Icon.Wand size={14}/> Simular escenarios de oferta</button>
          </div>
        </div>
      </div>

      <div>
        <div className="section-title" style={{marginBottom:10}}>Simulación de escenarios · Budgie</div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14}}>
          {[
            { title:'Base',  baja:'12,4%', precio:'1.640.500 €', margen:'9,2%', tone:'success', desc:'Escenario por defecto. Compromisos al mínimo + colchón de holgura.', sel:true },
            { title:'Agresivo', baja:'18,5%', precio:'1.526.200 €', margen:'4,8%', tone:'warning', desc:'Más puntos en precio (+1,9). Riesgo de baja anómala bajo.', sel:false },
            { title:'Margen reforzado', baja:'8,0%', precio:'1.722.000 €', margen:'14,1%', tone:'info', desc:'Reduce 2 pts en precio. Compensa con +600 h mentoría comprometidas.', sel:false },
          ].map((sc,i) => (
            <div key={i} className="card card-pad" style={{position:'relative', borderColor: sc.sel ? 'var(--line-3)' : 'var(--line-1)'}}>
              {sc.sel && <span className="sem sem-success" style={{position:'absolute', top:14, right:14, fontSize:10}}>Seleccionado</span>}
              <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:10}}>
                <span className={`sem sem-${sc.tone==='success'?'success':sc.tone==='warning'?'warning':'info'}`} style={{padding:'2px 8px'}}>Escenario {sc.title}</span>
              </div>
              <div className="font-display" style={{fontSize:36, lineHeight:1}}>{sc.precio}</div>
              <div style={{display:'flex', justifyContent:'space-between', marginTop:14, paddingTop:12, borderTop:'1px solid var(--line-1)'}}>
                <div><div style={{fontSize:11, color:'var(--text-3)', letterSpacing:'0.05em', textTransform:'uppercase'}}>Baja</div><div className="font-mono" style={{fontSize:13, marginTop:2}}>{sc.baja}</div></div>
                <div><div style={{fontSize:11, color:'var(--text-3)', letterSpacing:'0.05em', textTransform:'uppercase'}}>Margen</div><div className="font-mono" style={{fontSize:13, marginTop:2}}>{sc.margen}</div></div>
              </div>
              <div style={{fontSize:12, color:'var(--text-3)', marginTop:14, lineHeight:1.5}}>{sc.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BudgetRow({ label, value, sub, tone='neutral' }) {
  return (
    <div>
      <div style={{display:'flex', alignItems:'baseline', justifyContent:'space-between'}}>
        <span style={{fontSize:12.5, color:'var(--text-2)'}}>{label}</span>
        <span className={`font-mono`} style={{fontSize:14, color: tone==='success'?'var(--sem-success)':tone==='warning'?'var(--sem-warning)':'var(--text-1)'}}>{value}</span>
      </div>
      <div style={{fontSize:11, color:'var(--text-4)', marginTop:3}}>{sub}</div>
    </div>
  );
}

// ---------- Tab: Criteria ----------
function SandetelCriteria() {
  const s = window.MockData.SANDETEL;
  const subjTotal = s.criteriaSubj.reduce((sum,r) => sum + r.max, 0);
  const subjOurs = s.criteriaSubj.reduce((sum,r) => sum + r.ours, 0);
  const autoTotal = s.criteriaAuto.reduce((sum,r) => sum + r.max, 0);
  const autoOurs = s.criteriaAuto.reduce((sum,r) => sum + r.ours, 0);
  return (
    <div style={{display:'flex', flexDirection:'column', gap:24}}>
      <div className="callout callout-info">
        <div className="callout-icon"><Icon.Target size={16}/></div>
        <div>
          <div style={{fontWeight:500}}>Estimación de puntuación · {(subjOurs+autoOurs).toFixed(1)} / {subjTotal+autoTotal} pts</div>
          <div style={{fontSize:12.5, color:'var(--text-2)', marginTop:3}}>
            Sobre 2 (juicio de valor): <strong>{subjOurs.toFixed(1)} / {subjTotal}</strong> · Sobre 3 (fórmula automática): <strong>{autoOurs.toFixed(1)} / {autoTotal}</strong>.
            Umbral mínimo del Sobre 2: <strong>30 pts</strong> · estamos {subjOurs >= 30 ? 'por encima' : 'por debajo'}.
          </div>
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:18}}>
        <div className="card">
          <div style={{padding:'14px 18px', borderBottom:'1px solid var(--line-1)'}}>
            <div className="section-title">Sobre 2 · Juicio de valor</div>
            <div style={{fontSize:12, color:'var(--text-3)', marginTop:2}}>{subjTotal} pts · umbral {s.criteriaSubj.length ? '30' : ''}</div>
          </div>
          <CriteriaTable rows={s.criteriaSubj}/>
        </div>
        <div className="card">
          <div style={{padding:'14px 18px', borderBottom:'1px solid var(--line-1)'}}>
            <div className="section-title">Sobre 3 · Fórmula automática</div>
            <div style={{fontSize:12, color:'var(--text-3)', marginTop:2}}>{autoTotal} pts · vinculante</div>
          </div>
          <CriteriaTable rows={s.criteriaAuto}/>
        </div>
      </div>

      {/* Score gauge */}
      <div className="card card-pad">
        <div className="section-title" style={{marginBottom:12}}>Distribución vs adjudicaciones previas (similares)</div>
        <div style={{display:'grid', gridTemplateColumns:'2fr 1fr', gap:24, alignItems:'center'}}>
          <ScoreBarChart/>
          <div>
            <div style={{display:'flex', alignItems:'center', gap:10}}>
              <Icon.Crown size={16} style={{color:'var(--sem-warning)'}}/>
              <span style={{fontSize:13}}>Adjudicación estimada en <strong>87,5 pts</strong> (mediana de 8 contratos similares 2022-2025)</span>
            </div>
            <div style={{display:'flex', alignItems:'center', gap:10, marginTop:10}}>
              <Icon.Activity size={16} style={{color:'var(--sem-success)'}}/>
              <span style={{fontSize:13}}>Nuestra propuesta estimada: <strong>83,0 pts</strong> · gap de 4,5 pts</span>
            </div>
            <div style={{display:'flex', alignItems:'center', gap:10, marginTop:10}}>
              <Icon.Lightning size={16} style={{color:'var(--sem-info)'}}/>
              <span style={{fontSize:13}}>Margen de mejora: §3.4 (mejora continua) + reforzar §5 (plataforma)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CriteriaTable({ rows }) {
  return (
    <table className="tbl">
      <thead><tr><th>Criterio</th><th style={{width:60, textAlign:'right'}}>Máx.</th><th style={{width:90, textAlign:'right'}}>Estimado</th></tr></thead>
      <tbody>
        {rows.map((r,i) => (
          <tr key={i}>
            <td>
              <div style={{fontSize:13}}>{r.label}</div>
              <div style={{fontSize:11, color:'var(--text-4)', marginTop:3}}>{r.note}</div>
            </td>
            <td style={{textAlign:'right', fontFamily:'JetBrains Mono', color:'var(--text-3)'}}>{r.max}</td>
            <td style={{textAlign:'right'}}>
              <div style={{display:'inline-flex',alignItems:'center', gap:6}}>
                <ProgressBar value={(r.ours/r.max)*100} tone={(r.ours/r.max)>0.75?'success':(r.ours/r.max)>0.5?'warning':'danger'}/>
                <span className="font-mono" style={{fontSize:12.5}}>{r.ours.toFixed(1).replace('.', ',')}</span>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ScoreBarChart() {
  // Distribution bars
  const dist = [
    { label:'p10', value:74 },
    { label:'p25', value:79 },
    { label:'p50', value:87 },
    { label:'p75', value:93 },
    { label:'p90', value:97 },
  ];
  const ours = 83;
  return (
    <div style={{position:'relative', height:160, padding:'20px 0'}}>
      {/* Axis line */}
      <div style={{position:'absolute', left:0, right:0, bottom:30, height:1, background:'var(--line-1)'}}></div>
      {/* Distribution dots */}
      {dist.map((d,i)=>{
        const left = ((d.value-60)/(100-60))*100;
        return (
          <div key={i} style={{position:'absolute', left: left+'%', bottom:24, transform:'translateX(-50%)'}}>
            <div style={{width:6, height:14, borderRadius:2, background:'var(--text-4)'}}></div>
            <div style={{fontSize:10, color:'var(--text-4)', marginTop:4, fontFamily:'JetBrains Mono'}}>{d.value}</div>
            <div style={{fontSize:9.5, color:'var(--text-4)', marginTop:1}}>{d.label}</div>
          </div>
        );
      })}
      {/* Our score */}
      <div style={{position:'absolute', left: ((ours-60)/(100-60))*100 + '%', bottom:24, transform:'translateX(-50%)'}}>
        <div style={{position:'absolute', bottom:'100%', left:'50%', transform:'translateX(-50%)', whiteSpace:'nowrap', background:'var(--sem-success)', color:'var(--text-inv)', padding:'2px 7px', borderRadius:4, fontSize:11, marginBottom:4}}>
          Nuestra estimación · {ours}
        </div>
        <div style={{width:2, height:120, background:'var(--sem-success)'}}></div>
      </div>
      {/* Min threshold */}
      <div style={{position:'absolute', left: ((60-60)/(100-60))*100 + '%', bottom:24}}>
        <div style={{width:2, height:120, background:'var(--sem-danger)', opacity:0.5, borderLeft:'2px dashed var(--sem-danger)'}}></div>
      </div>
    </div>
  );
}

// ---------- Tab: Team ----------
function SandetelTeam() {
  const s = window.MockData.SANDETEL;
  const totalHours = s.team.reduce((sum,r)=> sum + r.hours, 0);
  const totalCost = s.team.reduce((sum,r)=> sum + r.hours * r.rate, 0);
  return (
    <div style={{display:'flex', flexDirection:'column', gap:24}}>
      <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14}}>
        <BigKpi label="Horas totales" value={totalHours.toLocaleString('es-ES')} unit="h" sub="Sobre 3 años (12 m + prórrogas)"/>
        <BigKpi label="Coste personal" value={(totalCost/1_000_000).toFixed(2).replace('.',',')} unit="M€" sub="A tarifa interna"/>
        <BigKpi label="Perfiles diferentes" value="8" unit="roles" sub="6 senior + 2 junior"/>
        <BigKpi label="Tarifa media" value={(totalCost/totalHours).toFixed(2).replace('.',',')} unit="€/h" sub="Ponderada por dedicación"/>
      </div>
      <div className="card" style={{overflow:'hidden'}}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Perfil</th>
              <th style={{width:160}}>Categoría</th>
              <th style={{width:120, textAlign:'right'}}>Horas máx.</th>
              <th style={{width:120, textAlign:'right'}}>Tarifa €/h</th>
              <th style={{width:140, textAlign:'right'}}>Coste estimado</th>
              <th style={{width:200}}>Distribución</th>
            </tr>
          </thead>
          <tbody>
            {s.team.map((r,i) => (
              <tr key={i}>
                <td><strong>{r.role}</strong></td>
                <td><span className="chip" style={{fontSize:11}}>{r.profile}</span></td>
                <td style={{textAlign:'right', fontFamily:'JetBrains Mono'}}>{r.hours.toLocaleString('es-ES')}</td>
                <td style={{textAlign:'right', fontFamily:'JetBrains Mono', color:'var(--text-3)'}}>{r.rate.toFixed(2).replace('.', ',')}</td>
                <td style={{textAlign:'right', fontFamily:'JetBrains Mono', fontWeight:500}}>{(r.hours*r.rate).toLocaleString('es-ES', {maximumFractionDigits:0})} €</td>
                <td>
                  <div className="progress"><div style={{width: (r.hours/2400*100) + '%'}}></div></div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Tab: ANS ----------
function SandetelANS() {
  const s = window.MockData.SANDETEL;
  return (
    <div style={{display:'flex', flexDirection:'column', gap:24}}>
      <div className="callout callout-warn">
        <div className="callout-icon"><Icon.AlertTriangle size={16}/></div>
        <div>
          <div style={{fontWeight:500}}>Acumulación máxima teórica de penalizaciones: hasta <span className="font-mono">83.000 €</span></div>
          <div style={{fontSize:12.5, marginTop:3}}>Equivale al <strong>4,4% del presupuesto base</strong>. Conviene dimensionar la oferta económica con holgura suficiente.</div>
        </div>
      </div>
      <div className="card" style={{overflow:'hidden'}}>
        <table className="tbl">
          <thead><tr>
            <th>Código</th><th style={{width:100}}>Tipo</th><th>Indicador</th><th style={{width:140}}>Objetivo</th><th style={{width:200, textAlign:'right'}}>Penalidad máxima</th>
          </tr></thead>
          <tbody>
            {s.ans.map((r) => (
              <tr key={r.code}>
                <td className="font-mono" style={{fontSize:12, color:'var(--text-2)'}}>{r.code}</td>
                <td>
                  <span className={`sem ${r.kind==='Gestión'?'sem-info':'sem-warning'}`} style={{padding:'2px 6px', fontSize:11}}>{r.kind}</span>
                </td>
                <td>{r.indicator}</td>
                <td className="font-mono" style={{fontSize:12.5}}>{r.target}</td>
                <td style={{textAlign:'right', fontFamily:'JetBrains Mono', color:'var(--sem-danger)'}}>{r.penalty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Tab: Risks ----------
function SandetelRisks() {
  const s = window.MockData.SANDETEL;
  return (
    <div style={{display:'flex', flexDirection:'column', gap:24}}>
      <div style={{display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:18}}>
        <div>
          <div className="section-title" style={{marginBottom:10}}>Riesgos identificados · {s.risks.length}</div>
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            {s.risks.map((r,i) => <RiskRow key={i} risk={r}/>)}
          </div>
        </div>
        <div>
          <div className="section-title" style={{marginBottom:10}}>Risk radar · probabilidad × impacto</div>
          <RiskRadar risks={s.risks}/>
          <div style={{display:'flex', gap:8, marginTop:14, fontSize:11.5, color:'var(--text-3)'}}>
            <span className="sem sem-danger">Alto</span>
            <span className="sem sem-warning">Medio</span>
            <span className="sem sem-success">Bajo</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RiskRow({ risk }) {
  const tone = risk.level === 'high' ? 'sem-danger' : risk.level === 'medium' ? 'sem-warning' : 'sem-success';
  return (
    <div className="card" style={{padding:'14px', display:'grid', gridTemplateColumns:'8px 1fr', gap:14, alignItems:'flex-start'}}>
      <div style={{height:'100%', borderRadius:2, background: `var(--${tone.replace('sem-','sem-')})`}}></div>
      <div>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <strong style={{fontSize:13.5}}>{risk.name}</strong>
          <span className={`sem ${tone}`}>{risk.level === 'high' ? 'ALTO' : risk.level === 'medium' ? 'MEDIO' : 'BAJO'}</span>
        </div>
        <div style={{display:'flex', gap:14, marginTop:6, fontSize:11, color:'var(--text-3)'}}>
          <span>Prob. <strong style={{color:'var(--text-2)'}}>{risk.prob}</strong></span>
          <span>Impacto <strong style={{color:'var(--text-2)'}}>{risk.impact}</strong></span>
        </div>
        <div style={{fontSize:12.5, color:'var(--text-2)', marginTop:8, lineHeight:1.5}}>
          <strong style={{color:'var(--text-1)'}}>Mitigación · </strong>{risk.mitigation}
        </div>
      </div>
    </div>
  );
}

function RiskRadar({ risks }) {
  // Map prob/impact to grid 5x5
  const probs = ['baja','media','alta'];
  const impacts = ['bajo','medio','alto'];
  // Place risks in cells
  const cellRisks = (p, i) => risks.filter(r => r.prob === p && r.impact === i);
  // Tone by position
  const tone = (p, i) => {
    const score = (probs.indexOf(p)+1) * (impacts.indexOf(i)+1);
    if (score >= 6) return 'sem-danger';
    if (score >= 3) return 'sem-warning';
    return 'sem-success';
  };
  return (
    <div style={{display:'grid', gridTemplateColumns:'auto repeat(3, 1fr)', gap:1, background:'var(--line-1)', border:'1px solid var(--line-1)', borderRadius:8, overflow:'hidden'}}>
      <div className="risk-cell header" style={{gridColumn:'1', gridRow:'1'}}></div>
      {impacts.map(i => <div key={i} className="risk-cell header">{i}</div>)}
      {[...probs].reverse().map(p => (
        <React.Fragment key={p}>
          <div className="risk-cell label" style={{minHeight:80}}>{p}</div>
          {impacts.map(i => {
            const cell = cellRisks(p, i);
            const t = tone(p, i);
            return (
              <div key={p+i} className="risk-cell" style={{ background: cell.length ? `var(--${t.replace('sem-','sem-')}-bg)` : undefined, minHeight:80 }}>
                {cell.map((r, idx) => (
                  <div key={idx} className={`sem ${t}`} style={{display:'inline-block', marginRight:4, marginBottom:4, fontSize:10}}>
                    {r.name.slice(0,18)}{r.name.length>18?'…':''}
                  </div>
                ))}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}

// ---------- Tab: Deliverables ----------
function SandetelDeliverables() {
  const s = window.MockData.SANDETEL;
  return (
    <div className="card" style={{overflow:'hidden'}}>
      <table className="tbl">
        <thead><tr><th style={{width:70}}>Código</th><th>Entregable</th><th style={{width:160}}>Periodicidad</th><th style={{width:200}}>Responsable</th></tr></thead>
        <tbody>
          {s.deliverables.map((d) => (
            <tr key={d.code}>
              <td className="font-mono" style={{fontSize:12, color:'var(--text-2)'}}>{d.code}</td>
              <td>{d.name}</td>
              <td><span className="chip">{d.freq}</span></td>
              <td style={{color:'var(--text-2)'}}>{d.owner}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Tab: Documents ----------
function SandetelDocuments() {
  const docs = [
    { name:'PPT_SANDETEL_25-00158_Lote2.pdf',    size:'4,2 MB', type:'pliego',      date:'15 may'},
    { name:'PCAP_SANDETEL_25-00158.pdf',          size:'1,8 MB', type:'pliego',      date:'15 may'},
    { name:'Anexo I — Memoria económica.pdf',     size:'820 KB', type:'pliego',      date:'15 may'},
    { name:'Anexo II — Solvencia.pdf',            size:'640 KB', type:'pliego',      date:'15 may'},
    { name:'Análisis transversal · Lizzie.docx',   size:'124 KB', type:'asistente',  date:'27 may'},
    { name:'One-page · Onnie.pdf',                 size:'320 KB', type:'asistente',  date:'27 may'},
    { name:'Tareas técnicas · Tecnie.docx',        size:'90 KB',  type:'asistente',  date:'27 may'},
    { name:'Análisis económico · Budgie.xlsx',     size:'48 KB',  type:'asistente',  date:'27 may'},
    { name:'Memorias previas referenciadas.zip',   size:'12 MB',  type:'biblioteca', date:'27 may'},
    { name:'Tarifario interno aplicado.xlsx',      size:'62 KB',  type:'biblioteca', date:'27 may'},
    { name:'Acta comité go/no-go (borrador).docx', size:'18 KB',  type:'interno',    date:'26 may'},
    { name:'Notas de campo entrevista cliente.md', size:'8 KB',   type:'interno',    date:'25 may'},
    { name:'Plan de captación territorial.pdf',    size:'1,2 MB', type:'interno',    date:'24 may'},
    { name:'Plantilla memoria técnica aplicada.docx', size:'2,1 MB', type:'biblioteca', date:'24 may'},
  ];
  const tone = { pliego:'sem-info', asistente:'sem-violet', biblioteca:'sem-warning', interno:'sem-neutral' };
  return (
    <div className="card" style={{overflow:'hidden'}}>
      <table className="tbl">
        <thead><tr><th></th><th>Documento</th><th style={{width:120}}>Origen</th><th style={{width:100}}>Tamaño</th><th style={{width:110}}>Fecha</th><th style={{width:36}}></th></tr></thead>
        <tbody>
          {docs.map((d,i) => (
            <tr key={i}>
              <td style={{width:32}}><Icon.FileText size={14}/></td>
              <td>{d.name}</td>
              <td><span className={`sem ${tone[d.type]}`} style={{fontSize:11}}>{d.type}</span></td>
              <td className="font-mono" style={{fontSize:12, color:'var(--text-3)'}}>{d.size}</td>
              <td style={{color:'var(--text-3)', fontSize:12}}>{d.date}</td>
              <td><button className="btn btn-ghost btn-icon" onClick={()=>notify('Descargar, renombrar o eliminar documento', 'MoreH')}><Icon.MoreH size={13}/></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Generic / IIL dinámico ----------
const REC = {
  presentarse:               { label:'Presentarse', tone:'sem-success', icon:'CheckCircle' },
  presentarse_con_condiciones:{ label:'Presentarse con condiciones', tone:'sem-warning', icon:'AlertTriangle' },
  no_presentarse:            { label:'No presentarse', tone:'sem-danger', icon:'X' },
};

function BidDetailGeneric({ bid, back, embedded = false }) {
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pct, setPct] = useState(0);
  const [lots, setLots] = useState([]);
  const [parent, setParent] = useState(null);
  const [onePagerOpen, setOnePagerOpen] = useState(false);
  const onePagerRef = useRef(null);
  const { isAdmin, roles = [] } = useAuth();
  const canEdit = isAdmin || roles.some(r => ['redactor', 'revisor', 'firmante'].includes(r));

  useEffect(() => {
    if (!onePagerOpen) return;
    const onDoc = (e) => { if (onePagerRef.current && !onePagerRef.current.contains(e.target)) setOnePagerOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onePagerOpen]);

  const load = useCallback(async () => {
    try {
      const r = await fetchBid(bid.id);
      setRow(r);
      // Lotes hijos de esta licitación (si es matriz)
      try { setLots(await listBidLots(bid.id)); } catch (_) { setLots([]); }
      // Si esta entrada es un lote, cargamos su licitación matriz
      if (r?.parent_bid_id) {
        try { setParent(await fetchBid(r.parent_bid_id)); } catch (_) { setParent(null); }
      } else { setParent(null); }
      return r;
    } catch (_) { /* noop */ }
    finally { setLoading(false); }
  }, [bid.id]);


  useEffect(() => { load(); }, [load]);

  // Título resumido por IA: si la ficha ya está lista y el título oficial es
  // largo, se genera una vez al abrirla (también justo al terminar un análisis).
  const titledRef = useRef(false);
  useEffect(() => {
    if (titledRef.current || !canEdit || !row) return;
    if (row.analysis_status !== 'done' || row.short_title || String(row.title || '').length <= 60) return;
    titledRef.current = true;
    generateShortTitles({ bidIds: [bid.id] })
      .then((t) => { if (t[bid.id]) setRow((r) => ({ ...r, short_title: t[bid.id] })); })
      .catch((e) => console.warn('[lizzi] título resumido:', e?.message || e));
  }, [row, canEdit, bid.id]);

  // Refresco al terminar el análisis + polling mientras procesa
  useEffect(() => {
    const onAnalyzed = (e) => { if (e.detail === bid.id) load(); };
    window.addEventListener('lizzi:bid-analyzed', onAnalyzed);
    // Cambios hechos desde el chat de Lizzi (o el selector de estado).
    window.addEventListener('lizzi:bid-updated', onAnalyzed);
    return () => {
      window.removeEventListener('lizzi:bid-analyzed', onAnalyzed);
      window.removeEventListener('lizzi:bid-updated', onAnalyzed);
    };
  }, [bid.id, load]);

  const [titling, setTitling] = useState(false);
  const regenShortTitle = async () => {
    setTitling(true);
    try {
      await generateShortTitles({ bidIds: [bid.id], force: true });
      notify('Título resumido regenerado.', 'CheckCircle');
    } catch (e) {
      notify('No se pudo generar el título: ' + (e?.message || 'error'), 'AlertTriangle');
    } finally { setTitling(false); }
  };

  useEffect(() => {
    if (row?.analysis_status !== 'processing') return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [row?.analysis_status, load]);

  // Progreso REAL persistido en la BD; con un suave avance entre sondeos
  // para que la barra no se vea congelada.
  useEffect(() => {
    if (row?.analysis_status !== 'processing') { setPct(0); return; }
    const real = typeof row?.analysis_pct === 'number' ? row.analysis_pct : 6;
    setPct((p) => Math.max(p, real || 6));
    const id = setInterval(() => {
      setPct((p) => (p >= 95 ? p : +(p + Math.max(0.4, (95 - p) * 0.04)).toFixed(1)));
    }, 600);
    return () => clearInterval(id);
  }, [row?.analysis_status, row?.analysis_pct]);


  const status = row?.analysis_status;
  const analysis = row?.analysis;

  const reanalyze = async () => {
    setReanalyzing(true);
    setRow((r) => ({ ...r, analysis_status: 'processing', analysis_phase: 'analyzing', analysis_pct: 10, analysis_error: null }));
    setPct(10);
    try {
      const res = await retryAnalysis(bid.id, row?.analysis_source_url || null);
      if (res?.status === 'done') notify('Ficha generada por Lizzi.', 'CheckCircle');
      else if (res?.status === 'error') notify('El análisis falló: ' + (res.message || 'error'), 'AlertTriangle');
      await load();
    } catch (e) {
      notify('No se pudo analizar: ' + (e?.message || 'error'), 'AlertTriangle');
      await load();
    } finally {
      setReanalyzing(false);
    }
  };

  return (
    <div className="viewport-inner fade-in">
      {!embedded && (
      <>
      <div style={{display:'flex',alignItems:'center',gap:8, marginBottom:12}}>
        <button className="btn btn-ghost" onClick={back} style={{padding:'4px 8px', fontSize:12}}><Icon.ChevronLeft size={13}/> Licitaciones</button>
        <span className="font-mono" style={{fontSize:12, color:'var(--text-4)'}}>{bid.code}</span>
      </div>
      <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'14px 20px', flexWrap:'wrap'}}>
        <div style={{minWidth:0, flex:'1 1 420px'}}>
          <div className="page-eyebrow">{row?.entity_short || row?.entity || bid.entityShort || bid.entity || '—'}{(row?.region || bid.region) ? ` · ${row?.region || bid.region}` : ''}</div>
          <h1 className="page-title" title={row?.title || bid.title} style={{fontSize:'clamp(22px, 2.4vw, 30px)', lineHeight:1.15, maxWidth:820, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden', textOverflow:'ellipsis'}}>{bidTitle(row) || bidTitle(bid)}</h1>
          {(row?.short_title || bid.shortTitle) && (
            <div title={row?.title || bid.title} style={{fontSize:12.5, color:'var(--text-3)', marginTop:4, maxWidth:820, lineHeight:1.45, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}>
              <span style={{color:'var(--text-4)'}}>Título oficial:</span> {row?.title || bid.title}
              {canEdit && <button className="btn btn-ghost" disabled={titling} onClick={regenShortTitle} title="Regenerar el título resumido con IA" style={{fontSize:11, padding:'1px 6px', marginLeft:6}}><Icon.Sparkles size={11}/> {titling ? 'Generando…' : 'Regenerar'}</button>}
            </div>
          )}
          {!(row?.short_title || bid.shortTitle) && canEdit && (row?.title || bid.title || '').length > 60 && (
            <button className="btn btn-ghost" disabled={titling} onClick={regenShortTitle} style={{fontSize:11.5, padding:'2px 8px', marginTop:4}}><Icon.Sparkles size={11}/> {titling ? 'Generando…' : 'Resumir título con IA'}</button>
          )}
          <div style={{display:'flex', gap:10, marginTop:8, flexWrap:'wrap', alignItems:'center'}}>
            {canEdit && row
              ? <StatusSelect bid={{ id: bid.id, status: row.status }} onChanged={(st) => setRow((r) => ({ ...r, status: st }))}/>
              : <StatusChip status={row?.status || bid.status}/>}
            {status === 'done' && <span className="chip" style={{color:'var(--sem-success)', borderColor:'var(--sem-success)'}}><Icon.CheckCircle size={11}/> Ficha lista{row?.analysis_model ? ` · ${row.analysis_model}` : ''}</span>}
            {status === 'processing' && <span className="chip" style={{color:'var(--sem-violet)', borderColor:'var(--sem-violet)'}}><span className="spin" style={{display:'inline-flex'}}><Icon.Sparkles size={11}/></span> Analizando con Lizzi</span>}
            {status === 'error' && <span className="chip" style={{color:'var(--sem-danger)', borderColor:'var(--sem-danger)'}}><Icon.AlertTriangle size={11}/> Error en análisis</span>}
            {(row?.entity || bid.entity) && <span className="chip"><Icon.Building size={11}/> {row?.entity || bid.entity}</span>}
            <span className="chip"><Icon.Hash size={11}/> {row?.code || bid.code}</span>
            {(row?.lot || bid.lot) && <span className="chip"><Icon.Layers size={11}/> {row?.lot || bid.lot}</span>}
            {(row?.company || bid.company) && <span className="chip"><Icon.Briefcase size={11}/> {row?.company || bid.company}</span>}
            {(row?.region || bid.region) && <span className="chip"><Icon.MapPin size={11}/> {row?.region || bid.region}</span>}
            {(row?.deadline || bid.deadline) && <span className="chip"><Icon.Calendar size={11}/> {fmtDateFull(row?.deadline || bid.deadline)}</span>}
            {(Number(row?.budget_base) > 0 || bid.budgetBase > 0) && <span className="chip"><Icon.Coins size={11}/> {fmtMoney(Number(row?.budget_base) || bid.budgetBase)}</span>}
            {(row?.analysis_source_url || bid.analysisSourceUrl) && <a className="chip" href={row?.analysis_source_url || bid.analysisSourceUrl} target="_blank" rel="noreferrer" style={{textDecoration:'none', cursor:'pointer'}}><Icon.Globe size={11}/> Web</a>}
          </div>
          {parent && (
            <div style={{marginTop:10}}>
              <button className="btn btn-ghost" style={{padding:'4px 8px', fontSize:12}} onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-bid', { detail: parent.id }))}>
                <Icon.ChevronLeft size={13}/> Volver a la licitación matriz: {parent.title}
              </button>
            </div>
          )}


        </div>
        <div style={{display:'flex', gap:8, flexWrap:'wrap', justifyContent:'flex-end', alignItems:'flex-start', maxWidth:'100%'}}>
          <button className="btn btn-outline" onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-chat'))} title="Chat con Lizzi sobre esta licitación (⌘J)"><Icon.MessageSquare size={14}/> Preguntar a Lizzi</button>
          {canEdit && <button className="btn btn-outline" onClick={()=>setEditing(true)}><Icon.Edit size={14}/> Editar</button>}
          <div ref={onePagerRef} style={{position:'relative'}}>
            <button
              className="btn btn-primary"
              disabled={!analysis}
              onClick={()=>setOnePagerOpen(v=>!v)}
              title="One-Pager visual de 5-6 páginas ideal para compartir"
            >
              <Icon.Sparkles size={14}/> One-Pager <Icon.ChevronDown size={12}/>
            </button>
            {onePagerOpen && (
              <div style={{
                position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:50,
                minWidth: 240, background:'var(--bg-1)',
                border:'1px solid var(--line-2)', borderRadius:10,
                boxShadow:'0 12px 30px rgba(0,0,0,0.18)', padding:6,
                color:'var(--text-1)',
              }}>
                <button
                  className="btn btn-ghost"
                  style={{width:'100%', justifyContent:'flex-start', padding:'8px 10px', fontSize:13, color:'var(--text-1)'}}
                  onClick={()=>{ setOnePagerOpen(false); downloadOnePagerWord(row, analysis); notify('Descargando One-Pager en Word.', 'Download'); }}
                >
                  <Icon.FileText size={14}/> Descargar Word (.doc)
                </button>
                <button
                  className="btn btn-ghost"
                  style={{width:'100%', justifyContent:'flex-start', padding:'8px 10px', fontSize:13, color:'var(--text-1)'}}
                  onClick={()=>{ setOnePagerOpen(false); downloadOnePagerPdf(row, analysis); notify('Abre el diálogo de impresión y elige "Guardar como PDF".', 'Download'); }}
                >
                  <Icon.Download size={14}/> Descargar PDF
                </button>
                <div style={{padding:'6px 10px 4px', fontSize:11, color:'var(--text-3)', borderTop:'1px solid var(--line-1)', marginTop:4}}>
                  Resumen visual de 5-6 páginas
                </div>
              </div>
            )}
          </div>
          <button className="btn btn-outline" disabled={!analysis} onClick={()=>{ downloadIILWord(row, analysis); notify('Descargando informe completo (IIL).', 'Download'); }} title="Informe Inteligente de Licitación completo"><Icon.Download size={14}/> IIL completo</button>
          <button className="btn btn-outline" disabled={reanalyzing || status==='processing'} onClick={reanalyze}><Icon.Sparkles size={14}/> {status==='processing'||reanalyzing ? 'Analizando…' : 'Regenerar análisis'}</button>
        </div>
      </div>
      </>
      )}

      <EditBidModal open={editing} bid={row || bid} onClose={()=>setEditing(false)} onSaved={()=>{ load(); refreshBidInStore(bid.id); }}/>

      {lots.length > 0 && (
        <div className="card" style={{marginTop:18, padding:16}}>
          <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:12}}>
            <Icon.Layers size={14} style={{color:'var(--sem-violet)'}}/>
            <span className="font-display" style={{fontSize:15}}>Lotes de esta licitación</span>
            <span className="chip" style={{fontSize:10.5}}>{lots.length}</span>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            {lots.map((l) => (
              <div key={l.id} className="card" style={{padding:'10px 12px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12}}
                onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-bid', { detail: l.id }))}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:13, color:'var(--text-1)'}} title={l.title}>{bidTitle(l)}</div>
                  <div style={{fontSize:11, color:'var(--text-3)', marginTop:2}}>{l.budgetBase > 0 ? fmtMoney(l.budgetBase) : 'Sin presupuesto'}</div>
                </div>
                <StatusChip status={l.status}/>
              </div>
            ))}
          </div>
        </div>
      )}



      {loading && <div style={{marginTop:40, textAlign:'center', color:'var(--text-3)'}}><Icon.Sparkles size={22}/><div style={{marginTop:10}}>Cargando ficha…</div></div>}

      {!loading && (status === 'processing') && (() => {
        const STEPS = [
          { key:'reading',   label:'Leyendo los pliegos',            desc:'Lizzi extrae texto, tablas y datos clave de cada documento.', icon:'FileText' },
          { key:'analyzing', label:'Analizando y cruzando datos',    desc:'Relaciona criterios, solvencia, economía y riesgos.',         icon:'Sparkles' },
          { key:'compiling', label:'Generando la ficha (11 secciones)', desc:'Redacta el informe ejecutivo completo.',                  icon:'Layers' },
        ];
        const ORDER = { uploading:0, reading:0, analyzing:1, compiling:2, done:3 };
        const activeIdx = ORDER[row?.analysis_phase] ?? 0;
        return (
        <div className="card" style={{marginTop:24, overflow:'hidden'}}>
          <div style={{position:'relative', padding:'28px 28px 22px', background:'linear-gradient(160deg, var(--primary-soft), transparent)', borderBottom:'1px solid var(--line-1)'}}>
            <div style={{display:'flex', alignItems:'center', gap:14}}>
              <div style={{width:48, height:48, borderRadius:14, background:'var(--bg-1)', border:'1px solid var(--line-2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>
                <span className="spin"><Icon.Sparkles size={22} style={{color:'var(--sem-violet)'}}/></span>
              </div>
              <div style={{minWidth:0}}>
                <div className="page-eyebrow" style={{margin:0, marginBottom:4}}>ANÁLISIS INTELIGENTE EN CURSO</div>
                <div className="font-display" style={{fontSize:19}}>Lizzi está analizando el pliego</div>
              </div>
            </div>
            <div style={{marginTop:18}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6}}>
                <span style={{fontSize:11.5, color:'var(--text-3)'}}>{STEPS[activeIdx]?.label || 'Generando informe'}</span>
                <span className="font-display-heavy" style={{fontSize:16, color:'var(--sem-violet)'}}>{Math.round(pct)}%</span>
              </div>
              <div style={{height:7, background:'var(--bg-2)', borderRadius:999, overflow:'hidden'}}>
                <div style={{height:'100%', width:`${Math.round(pct)}%`, borderRadius:999, transition:'width 500ms ease', background:'linear-gradient(90deg, var(--sem-violet), var(--primary))'}}/>
              </div>
            </div>
          </div>
          <div style={{padding:'18px 28px 24px', display:'flex', flexDirection:'column', gap:14}}>
            {STEPS.map((s, i) => {
              const state = i < activeIdx ? 'done' : i === activeIdx ? 'active' : 'idle';
              const Ico = Icon[s.icon];
              return (
                <div key={s.key} style={{display:'flex', alignItems:'flex-start', gap:12, opacity: state==='idle' ? 0.5 : 1}}>
                  <span style={{width:28, height:28, borderRadius:9, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
                    background: state==='done' ? 'var(--sem-success-soft, var(--bg-2))' : state==='active' ? 'var(--primary-soft)' : 'var(--bg-2)',
                    color: state==='done' ? 'var(--sem-success)' : state==='active' ? 'var(--sem-violet)' : 'var(--text-4)'}}>
                    {state==='done' ? <Icon.Check size={14}/> : <Ico size={14} className={state==='active' ? 'pulse' : ''}/>}
                  </span>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:13.5, fontWeight: state==='active' ? 600 : 500, color: state==='idle' ? 'var(--text-4)' : 'var(--text-1)'}}>{s.label}</div>
                    <div style={{fontSize:12, color:'var(--text-3)', marginTop:2}}>{s.desc}</div>
                  </div>
                </div>
              );
            })}
            <div style={{fontSize:11.5, color:'var(--text-4)', marginTop:4}}>Se actualiza solo · puedes seguir trabajando en otras pantallas.</div>
          </div>
        </div>
        );
      })()}


      {!loading && (status === 'error') && (
        <div className="callout callout-warn" style={{marginTop:24}}>
          <div className="callout-icon"><Icon.AlertTriangle size={16}/></div>
          <div>
            <div style={{fontWeight:500, marginBottom:4}}>El análisis se detuvo</div>
            <div style={{fontSize:12.5, lineHeight:1.5}}>{row?.analysis_error || 'Puede deberse a un documento ilegible o a un error temporal de la IA.'} Reintenta el análisis sin volver a subir los documentos.</div>
            <button className="btn btn-outline" style={{marginTop:12}} disabled={reanalyzing} onClick={reanalyze}><Icon.Sparkles size={13}/> {reanalyzing ? 'Reintentando…' : 'Reintentar análisis'}</button>
          </div>
        </div>
      )}

      {!loading && !analysis && status !== 'processing' && status !== 'error' && (
        <div className="callout callout-info" style={{marginTop:24}}>
          <div className="callout-icon"><Icon.Info size={15}/></div>
          <div>Esta licitación todavía no tiene una ficha generada. Pulsa <strong>Regenerar análisis</strong> para que Lizzi la cree a partir de los documentos subidos.</div>
        </div>
      )}

      {!loading && status === 'done' && Array.isArray(row?.analysis_warnings) && row.analysis_warnings.length > 0 && (
        <div className="callout callout-warn" style={{marginTop:24}}>
          <div className="callout-icon"><Icon.AlertTriangle size={15}/></div>
          <div>
            <div style={{fontWeight:500, marginBottom:4}}>Ficha lista con avisos</div>
            <ul style={{margin:'4px 0 0', paddingLeft:18, fontSize:12.5, lineHeight:1.6}}>
              {row.analysis_warnings.map((w, i) => <li key={i}>{String(w)}</li>)}
            </ul>
          </div>
        </div>
      )}

      {!loading && analysis && <IILView bid={row} a={analysis}/>}
    </div>
  );
}

// ---------- Render del IIL (11 secciones) ----------
function IILSection({ n, title, children }) {
  return (
    <div style={{marginTop:26}}>
      <div className="section-title" style={{marginBottom:12}}>§{n}. {title}</div>
      {children}
    </div>
  );
}

function MiniTable({ headers, rows }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="card" style={{overflow:'hidden', marginTop:10}}>
      <table className="tbl">
        <thead><tr>{headers.map((h,i)=><th key={i}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r,i)=>(
            <tr key={i}>{r.map((c,j)=><td key={j} style={{fontSize:12.5, color:'var(--text-2)'}}>{c || '—'}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Bullets({ items }) {
  if (!items || !items.length) return null;
  return (
    <ul style={{margin:'8px 0 0', paddingLeft:18, display:'flex', flexDirection:'column', gap:6}}>
      {items.map((t,i)=><li key={i} style={{fontSize:13, color:'var(--text-2)', lineHeight:1.5}}>{t}</li>)}
    </ul>
  );
}

function Para({ text }) {
  if (!text) return null;
  return <p style={{fontSize:13.5, lineHeight:1.6, color:'var(--text-2)', margin:'8px 0 0'}}>{text}</p>;
}

// Render ligero de markdown (encabezados, listas, negritas) sin dependencias.
function MarkdownLite({ text }) {
  if (!text || typeof text !== 'string') return null;
  const lines = text.split('\n');
  const out = [];
  let list = null;
  const inline = (s) => {
    const parts = String(s).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} style={{color:'var(--text-1)'}}>{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>);
  };
  const flush = (k) => { if (list) { out.push(<ul key={`u${k}`} style={{margin:'6px 0 10px', paddingLeft:18, display:'flex', flexDirection:'column', gap:5}}>{list}</ul>); list = null; } };
  lines.forEach((raw, i) => {
    const l = raw.trim();
    if (!l) { flush(i); return; }
    const h = l.match(/^(#{1,4})\s+(.*)$/);
    if (h) { flush(i); const lvl = h[1].length; out.push(<div key={i} className={lvl<=2?'section-title':''} style={{marginTop:16, marginBottom:6, fontSize:lvl<=2?14:13, fontWeight:700, color:'var(--text-1)'}}>{inline(h[2])}</div>); return; }
    const li = l.match(/^[-*]\s+(.*)$/);
    if (li) { if (!list) list = []; list.push(<li key={i} style={{fontSize:13.5, color:'var(--text-2)', lineHeight:1.55}}>{inline(li[1])}</li>); return; }
    flush(i);
    out.push(<p key={i} style={{fontSize:13.5, lineHeight:1.65, color:'var(--text-2)', margin:'8px 0 0'}}>{inline(l)}</p>);
  });
  flush('end');
  return <div>{out}</div>;
}

// Tab Evidencias: citas textuales del pliego con su sección y documento origen.
function TabEvidence({ evidence }) {
  const rows = nonEmptyArr(evidence) ? evidence : [];
  if (!rows.length) return null;
  return (
    <div className="fade-in stagger" style={{display:'flex', flexDirection:'column', gap:12}}>
      <div className="section-title" style={{marginBottom:2}}>Evidencias y citas textuales</div>
      <p style={{fontSize:12.5, color:'var(--text-3)', margin:0}}>Fragmentos literales de los pliegos que respaldan el análisis.</p>
      {rows.map((e, i) => (
        <div key={i} className="card card-pad" style={{borderLeft:'3px solid var(--primary)'}}>
          <div style={{display:'flex', justifyContent:'space-between', gap:10, flexWrap:'wrap', marginBottom:8}}>
            <span style={{fontSize:11.5, fontWeight:700, color:'var(--primary)', textTransform:'uppercase', letterSpacing:'0.04em'}}>{e.section || 'Sección'}</span>
            {e.source && <span style={{fontSize:11.5, color:'var(--text-3)'}}>{e.source}</span>}
          </div>
          <blockquote style={{margin:0, fontSize:13.5, lineHeight:1.6, color:'var(--text-2)', fontStyle:'italic'}}>“{e.quote}”</blockquote>
        </div>
      ))}
    </div>
  );
}

const SCEN_TONE = { success:'sem-success', warning:'sem-warning', info:'sem-info', danger:'sem-danger' };
const RISK_TONE = (p, i) => {
  const probs = ['baja','media','alta']; const imps = ['bajo','medio','alto'];
  const score = (probs.indexOf((p||'').toLowerCase())+1) * (imps.indexOf((i||'').toLowerCase())+1);
  if (score >= 6) return 'sem-danger';
  if (score >= 3) return 'sem-warning';
  return 'sem-success';
};

function nonEmptyArr(v){ return Array.isArray(v) && v.length > 0; }
function hasText(v){ return v && v !== 'Dato no encontrado en pliegos'; }

function IILView({ bid, a }) {
  const s = a.snapshot || {};
  const eco = a.economic || {};
  const crit = a.criteria || {};
  const team = a.team || {};
  const risks = a.risks || {};
  const deliver = a.deliverables || {};
  const docs = a.documents || {};
  const tech = a.technical || {};
  const inv = a.invoicing || {};
  const sub = a.submission || {};


  const tabDefs = [
    { id:'overview', label:'Resumen', icon:'Layout', show:true },
    { id:'go', label:'Análisis del Go', icon:'Target', show:true },
    { id:'technical', label:'Técnico', icon:'Cpu', show: nonEmptyArr(tech.modules) || nonEmptyArr(tech.otherScope) },
    // Siempre visible: incluye el presupuesto de la oferta (editable / generado por Lizzi).
    { id:'budget', label:'Presupuesto y facturación', icon:'Coins', show:true },
    { id:'criteria', label:'Criterios', icon:'Calculator', show: !!a.criteria && (nonEmptyArr(crit.subjective) || nonEmptyArr(crit.automatic) || nonEmptyArr(crit.priceScenarios) || nonEmptyArr(crit.roi)) || (bid && bid.go_data && bid.go_data.criteriaCalc) },
    { id:'team', label:'Equipo', icon:'Users', show: !!a.team && (nonEmptyArr(team.profiles) || nonEmptyArr(team.coverage) || hasText(team.summary)) },
    { id:'ans', label:'ANS y compromisos', icon:'Shield', show: !!a.commitments && (nonEmptyArr(a.commitments.items) || nonEmptyArr((a.solvency||{}).specialConditions)) },
    { id:'risks', label:'Riesgos', icon:'AlertTriangle', show: !!a.risks && nonEmptyArr(risks.items) },
    { id:'deliverables', label:'Entregables', icon:'Layers', show: nonEmptyArr(deliver.items) || nonEmptyArr(docs.criticalDates) },
    { id:'submission', label:'Presentación de ofertas', icon:'Send', show: (nonEmptyArr(sub.envelopes) || nonEmptyArr(sub.contamination)) || (!!a.documents && (nonEmptyArr(docs.envelope1)||nonEmptyArr(docs.envelope2)||nonEmptyArr(docs.envelope3)||hasText(docs.contaminationWarning))) },
    { id:'documents', label:'Documentos', icon:'FileText', show: !!a.documents && (nonEmptyArr(docs.envelope1)||nonEmptyArr(docs.envelope2)||nonEmptyArr(docs.envelope3)||hasText(docs.contaminationWarning)) },
    { id:'evidence', label:'Evidencias', icon:'Quote', show: nonEmptyArr(a.evidence) },
    { id:'detail', label:'Informe completo', icon:'Book', show:true },
  ].filter(t => t.show);

  const [tab, setTab] = useState('overview');
  const rec = REC[s.recommendation];
  const RecIco = rec ? Icon[rec.icon] : null;

  return (
    <div className="fade-in" style={{marginTop:24}}>
      {/* Recomendación destacada */}
      {a.snapshot && rec && (
        <div className="card card-pad" style={{display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:14, marginBottom:18,
          background:'linear-gradient(135deg, var(--bg-1), var(--bg-2))'}}>
          <div style={{display:'flex', alignItems:'center', gap:14, minWidth:0}}>
            <span className={`sem ${rec.tone}`} style={{width:44, height:44, borderRadius:12, justifyContent:'center', padding:0, flexShrink:0}}>
              {RecIco && <RecIco size={22}/>}
            </span>
            <div style={{minWidth:0}}>
              <div style={{fontSize:11.5, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em'}}>Recomendación de Lizzi</div>
              <div className="font-display" style={{fontSize:22, lineHeight:1.2, marginTop:2}}>{rec.label}</div>
            </div>
          </div>
          {s.confidence != null && (
            <div style={{textAlign:'right'}}>
              <div className="font-display-heavy" style={{fontSize:32, lineHeight:1, color:'var(--primary)'}}>{s.confidence}%</div>
              <div style={{fontSize:11, color:'var(--text-3)'}}>confianza</div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <TabNav tabDefs={tabDefs} tab={tab} setTab={setTab}/>



      {tab === 'overview'     && <TabOverview a={a}/>}
      {tab === 'go'           && <TabGo bid={bid} a={a}/>}
      {tab === 'technical'    && <TabTechnical tech={tech}/>}
      {tab === 'budget'       && <TabBudget bid={bid} eco={eco} inv={inv}/>}
      {tab === 'criteria'     && <TabCriteria crit={crit} eco={eco} bid={bid}/>}
      {tab === 'team'         && <TabTeam team={team}/>}
      {tab === 'ans'          && <TabAns commitments={a.commitments} solvency={a.solvency}/>}
      {tab === 'risks'        && <TabRisks risks={risks}/>}
      {tab === 'deliverables' && <TabDeliverables deliver={deliver} docs={docs}/>}
      {tab === 'submission'   && <TabSubmission sub={sub} docs={docs}/>}
      {tab === 'documents'    && <TabDocuments docs={docs} bid={bid}/>}
      {tab === 'evidence'     && <TabEvidence evidence={a.evidence}/>}
      {tab === 'detail'       && <IILFullReport bid={bid} a={a}/>}

      <div style={{marginTop:28, fontSize:11, color:'var(--text-4)'}}>
        Generado por Lizzi{bid?.analysis_model ? ` · ${bid.analysis_model}` : ''}{bid?.analysis_generated_at ? ` · ${fmtDateFull(bid.analysis_generated_at)}` : ''}
      </div>
    </div>
  );
}

// ---------- Tab: Resumen ----------
const HERO_TONES = ['sem-info','sem-warning','sem-violet','sem-success'];
const INSIGHT_CLASS = { warn:'callout-warn', info:'callout-info', success:'callout-success' };
const INSIGHT_ICON = { warn:'AlertTriangle', info:'Info', success:'CheckCircle' };

function TabOverview({ a }) {
  const s = a.snapshot || {};
  const commit = a.commitments || {};
  const sv = a.solvency || {};
  const heroKpis = (nonEmptyArr(s.heroKpis) ? s.heroKpis : (s.kpis || []).slice(0,4).map((k,i)=>({ ...k, tone: i===0?'primary':'neutral' }))).map(fixMoneyKpi);
  const indicators = nonEmptyArr(commit.indicators) ? commit.indicators : [];
  return (
    <div className="stagger" style={{display:'flex', flexDirection:'column', gap:24}}>
      {/* Hero KPIs */}
      {nonEmptyArr(heroKpis) && (
        <div>
          <div className="section-title" style={{marginBottom:14}}>Indicadores clave del pliego</div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(210px, 1fr))', gap:16}}>
            {heroKpis.slice(0,4).map((k,i)=>(
              <BigKpi key={i} label={k.label} value={k.value||'—'} unit={k.unit||''} sub={k.sub||''} tone={k.tone || (i===0?'primary':'neutral')}/>
            ))}
          </div>
        </div>
      )}

      {/* Compromisos cuantificables (indicadores con icono) */}
      {nonEmptyArr(indicators) && (
        <div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between', marginBottom:10}}>
            <div className="section-title">Compromisos cuantificables</div>
            <span className="chip"><Icon.Lightning size={11}/> Vinculantes en oferta</span>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(210px,1fr))', gap:14}}>
            {indicators.slice(0,4).map((k,i)=>{
              const Ico = Icon[k.icon] || Icon.Target;
              return (
                <div key={i} className="kpi" style={{position:'relative'}}>
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                    <span className={`sem ${HERO_TONES[i%HERO_TONES.length]}`} style={{padding:'2px 6px'}}><Ico size={11}/></span>
                    {k.unit && <span className="font-mono" style={{fontSize:11, color:'var(--text-4)'}}>{k.unit}</span>}
                  </div>
                  <div className="kpi-value" style={{marginTop:8}}>{k.value||'—'}</div>
                  <div style={{fontSize:11.5, color:'var(--text-2)', marginTop:2}}>{k.label}</div>
                  {k.sub && <div style={{fontSize:11, color:'var(--text-4)', marginTop:6}}>{k.sub}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Objeto + insights de Lizzi */}
      {(hasText(s.objective) || hasText(s.rationale) || nonEmptyArr(s.insights)) && (
        <div style={{display:'grid', gridTemplateColumns:'2fr 1fr', gap:16}}>
          <div className="card card-pad">
            <div className="section-title" style={{marginBottom:12}}>Objeto y alcance del contrato</div>
            <Para text={hasText(s.objective) ? s.objective : s.rationale}/>
            {nonEmptyArr(s.objectiveStats) && (
              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px,1fr))', gap:10, marginTop:16}}>
                {s.objectiveStats.slice(0,3).map((st,i)=>(
                  <div key={i} style={{padding:12, background:'var(--bg-2)', borderRadius:8}}>
                    <div style={{fontSize:11, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.05em'}}>{st.label}</div>
                    <div className="font-display" style={{fontSize:22, marginTop:2}}>{st.value}</div>
                    {st.sub && <div style={{fontSize:11, color:'var(--text-4)', marginTop:2}}>{st.sub}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:12}}>
            {nonEmptyArr(s.insights)
              ? s.insights.slice(0,3).map((ins,i)=>{
                  const Ico = Icon[INSIGHT_ICON[ins.tone] || 'Info'];
                  return (
                    <div key={i} className={`callout ${INSIGHT_CLASS[ins.tone] || 'callout-info'}`}>
                      <div className="callout-icon"><Ico size={16}/></div>
                      <div>
                        {ins.title && <div style={{fontWeight:500, marginBottom:4}}>{ins.title}</div>}
                        <div style={{fontSize:12.5, lineHeight:1.5}}>{ins.body}</div>
                      </div>
                    </div>
                  );
                })
              : (
                <div className="card card-pad">
                  <div className="section-title" style={{marginBottom:10}}>Lectura estratégica</div>
                  <Para text={s.rationale}/>
                </div>
              )}
          </div>
        </div>
      )}

      {/* Fortalezas vs atención */}
      {(nonEmptyArr(s.strengths) || nonEmptyArr(s.attention)) && (
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
          {nonEmptyArr(s.strengths) && (
            <div className="card card-pad">
              <div style={{fontSize:12.5, fontWeight:600, color:'var(--sem-success)', marginBottom:6, display:'flex', alignItems:'center', gap:6}}><Icon.CheckCircle size={14}/> Fortalezas</div>
              <Bullets items={s.strengths}/>
            </div>
          )}
          {nonEmptyArr(s.attention) && (
            <div className="card card-pad">
              <div style={{fontSize:12.5, fontWeight:600, color:'var(--sem-warning)', marginBottom:6, display:'flex', alignItems:'center', gap:6}}><Icon.AlertTriangle size={14}/> Puntos de atención</div>
              <Bullets items={s.attention}/>
            </div>
          )}
        </div>
      )}

      {/* Condiciones especiales de ejecución (tarjetas) */}
      {nonEmptyArr(sv.specialConditions) && (
        <div>
          <div className="section-title" style={{marginBottom:10}}>Condiciones especiales de ejecución</div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px,1fr))', gap:12}}>
            {sv.specialConditions.slice(0,6).map((c,i)=>(
              <ConditionCard key={i} icon={Icon[c.icon] ? c.icon : 'Shield'} tone={c.tone || HERO_TONES[i%HERO_TONES.length]}
                title={c.title} body={c.implication || c.level || ''} note={c.penalty ? `Penalización: ${c.penalty}` : (c.essential ? 'Obligación esencial' : '')}/>
            ))}
          </div>
        </div>
      )}

      {nonEmptyArr(a.general?.fields) && (
        <div>
          <div className="section-title" style={{marginBottom:10}}>Información general</div>
          <MiniTable headers={['Campo','Valor']} rows={a.general.fields.map(f=>[f.label,f.value])}/>
        </div>
      )}

      {nonEmptyArr(a.missingDocs) && (
        <div className="callout callout-warn">
          <div className="callout-icon"><Icon.AlertTriangle size={16}/></div>
          <div><div style={{fontWeight:500, marginBottom:4}}>Documentos clave no aportados</div><Bullets items={a.missingDocs}/></div>
        </div>
      )}
    </div>
  );
}

// ---------- Tab: Presupuesto y facturación ----------
function TabBudget({ bid, eco, inv = {} }) {
  const hasInvoicing = hasText(inv.howBilled) || hasText(inv.paymentTerms) || nonEmptyArr(inv.milestones) || inv.needsFinancing != null || hasText(inv.financingReason);
  return (
    <div className="stagger" style={{display:'flex', flexDirection:'column', gap:24}}>
      {bid?.id && <BudgetBuilder bid={bid}/>}
      {(nonEmptyArr(eco.breakdown) || nonEmptyArr(eco.calculations) || nonEmptyArr(eco.offerScenarios) || hasText(eco.abnormalLow) || hasText(eco.strategy) || hasInvoicing) && (
        <div style={{display:'flex', alignItems:'center', gap:12, marginTop:12}}>
          <div style={{fontSize:11, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--text-3)', whiteSpace:'nowrap'}}>Datos económicos del pliego</div>
          <div style={{flex:1, height:1, background:'var(--line-1)'}}/>
        </div>
      )}
      {nonEmptyArr(eco.breakdown) && (
        <div>
          <div className="section-title" style={{marginBottom:10}}>Estructura del presupuesto según el pliego</div>
          <MiniTable headers={['Concepto','Importe','%']} rows={eco.breakdown.map(b=>[b.concept,fixMoneyText(b.amount),b.pct])}/>
        </div>
      )}
      {nonEmptyArr(eco.calculations) && (
        <div>
          <div className="section-title" style={{marginBottom:10}}>Cálculos clave</div>
          <MiniTable headers={['Cálculo','Fórmula','Resultado']} rows={eco.calculations.map(c=>[c.name,fixMoneyText(c.formula),fixMoneyText(c.result)])}/>
        </div>
      )}
      {nonEmptyArr(eco.offerScenarios) && (
        <div>
          <div className="section-title" style={{marginBottom:10}}>Escenarios de oferta</div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px,1fr))', gap:14}}>
            {eco.offerScenarios.map((sc,i)=>(
              <div key={i} className="card card-pad" style={{position:'relative', borderColor: sc.recommended ? 'var(--line-3)' : 'var(--line-1)'}}>
                {sc.recommended && <span className="sem sem-success" style={{position:'absolute', top:14, right:14, fontSize:10}}>Recomendado</span>}
                <div className="font-display" style={{fontSize:18}}>{sc.title}</div>
                <div style={{display:'flex', gap:16, marginTop:12}}>
                  <div><div style={{fontSize:10.5, color:'var(--text-4)', textTransform:'uppercase'}}>Baja</div><div className="font-mono" style={{fontSize:15, marginTop:2}}>{sc.baja || '—'}</div></div>
                  <div><div style={{fontSize:10.5, color:'var(--text-4)', textTransform:'uppercase'}}>Precio</div><div className="font-mono" style={{fontSize:15, marginTop:2}}>{fixMoneyText(sc.price) || '—'}</div></div>
                  <div><div style={{fontSize:10.5, color:'var(--text-4)', textTransform:'uppercase'}}>Margen</div><div className="font-mono" style={{fontSize:15, marginTop:2}}>{sc.margin || '—'}</div></div>
                </div>
                {sc.description && <p style={{fontSize:12.5, color:'var(--text-2)', lineHeight:1.5, marginTop:12, marginBottom:0}}>{sc.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      {(hasText(eco.abnormalLow) || hasText(eco.strategy)) && (
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
          {hasText(eco.abnormalLow) && <div className="callout callout-warn"><div className="callout-icon"><Icon.AlertTriangle size={15}/></div><div><div style={{fontWeight:500, marginBottom:4}}>Baja anormal</div><div style={{fontSize:12.5, lineHeight:1.5}}>{eco.abnormalLow}</div></div></div>}
          {hasText(eco.strategy) && <div className="callout callout-info"><div className="callout-icon"><Icon.Info size={15}/></div><div><div style={{fontWeight:500, marginBottom:4}}>Estrategia económica</div><div style={{fontSize:12.5, lineHeight:1.5}}>{eco.strategy}</div></div></div>}
        </div>
      )}

      {hasInvoicing && (
        <div>
          <div className="section-title" style={{marginBottom:12, display:'flex', alignItems:'center', gap:8}}>
            <Icon.Coins size={15}/> Facturación y financiación
          </div>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
            <div className="card card-pad">
              <div style={{fontSize:11.5, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6}}>¿Cómo se factura?</div>
              <Para text={hasText(inv.howBilled) ? inv.howBilled : 'Dato no encontrado en pliegos'}/>
              {hasText(inv.paymentTerms) && (
                <div style={{marginTop:12}}>
                  <div style={{fontSize:11.5, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4}}>Plazos de pago y retenciones</div>
                  <Para text={inv.paymentTerms}/>
                </div>
              )}
            </div>
            <div className="card card-pad">
              <div style={{fontSize:11.5, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8}}>¿Necesita financiación?</div>
              <span className={`sem ${inv.needsFinancing === true ? 'sem-warning' : inv.needsFinancing === false ? 'sem-success' : 'sem-info'}`} style={{fontSize:12}}>
                {inv.needsFinancing === true ? 'Sí, requiere tesorería' : inv.needsFinancing === false ? 'No' : 'Sin determinar'}
              </span>
              {hasText(inv.financingReason) && <Para text={inv.financingReason}/>}
            </div>
          </div>
          {nonEmptyArr(inv.milestones) && (
            <div style={{marginTop:14}}>
              <div className="section-title" style={{marginBottom:8}}>Hitos de facturación</div>
              <MiniTable headers={['Hito','Importe','Momento']} rows={inv.milestones.map(m=>[m.milestone, m.amount, m.timing])}/>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

// ---------- Tab: Técnico (Módulos Data Viva) ----------
function TabTechnical({ tech = {} }) {
  const modules = tech.modules || [];
  const other = tech.otherScope || [];
  return (
    <div className="stagger" style={{display:'flex', flexDirection:'column', gap:20}}>
      <div className="callout callout-info">
        <div className="callout-icon"><Icon.Layers size={15}/></div>
        <div style={{fontSize:12.5, lineHeight:1.5}}>Servicios y actuaciones que exige la licitación, organizados según los Módulos de Data Viva.</div>
      </div>
      {nonEmptyArr(modules) && (
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(290px,1fr))', gap:14}}>
          {modules.map((m,i)=>(
            <div key={i} className="card card-pad">
              <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:8}}>
                <span className="sem sem-violet" style={{padding:'2px 6px'}}><Icon.Layers size={11}/></span>
                <div className="font-display" style={{fontSize:15}}>{m.module}</div>
              </div>
              {hasText(m.summary) && <Para text={m.summary}/>}
              {nonEmptyArr(m.actuations) && <Bullets items={m.actuations}/>}
            </div>
          ))}
        </div>
      )}
      {nonEmptyArr(other) && (
        <div className="card card-pad">
          <div className="section-title" style={{marginBottom:6}}>Otras actuaciones</div>
          <Bullets items={other}/>
        </div>
      )}
      {!nonEmptyArr(modules) && !nonEmptyArr(other) && (
        <Para text="Lizzi no ha identificado módulos de servicio en los pliegos analizados."/>
      )}
    </div>
  );
}

// ---------- Tab: Análisis del Go ----------
const GO_STATES = {
  go:      { label:'Go', tone:'sem-success', icon:'CheckCircle' },
  nogo:    { label:'No Go', tone:'sem-danger', icon:'X' },
  review:  { label:'A valorar', tone:'sem-warning', icon:'AlertTriangle' },
  pending: { label:'Pendiente de información', tone:'sem-info', icon:'Info' },
};

const GO_INPUT_STYLE = {
  width:'100%', background:'var(--bg-2)', border:'1px solid var(--line-1)',
  borderRadius:8, padding:'7px 10px', fontSize:12.5, color:'var(--text-1)',
};

function autoGoState(a) {
  const r = (a.snapshot || {}).recommendation;
  if (r === 'presentarse') return 'go';
  if (r === 'no_presentarse') return 'nogo';
  if (r === 'presentarse_con_condiciones') return 'review';
  return 'pending';
}

// Semáforo: success | warning | danger | neutral
const SIGNAL = {
  success: { color:'var(--sem-success)', label:'Favorable' },
  warning: { color:'var(--sem-warning)', label:'A vigilar' },
  danger:  { color:'var(--sem-danger)',  label:'Crítico' },
  neutral: { color:'var(--text-4)',      label:'Sin datos' },
};
const SIGNAL_RANK = { danger:3, warning:2, success:1, neutral:0 };
function worstSignal(list) {
  return list.reduce((w, x) => (SIGNAL_RANK[x] > SIGNAL_RANK[w] ? x : w), 'neutral');
}

// Interpreta el texto de estado de la viabilidad ("Viable", "Riesgo alto"…).
function signalFromText(t) {
  const v = String(t || '').toLowerCase();
  if (!v.trim()) return 'neutral';
  if (/inviable|no cumple|no viable|crític|critic|insuf|rojo|bloque|alto riesgo|riesgo alto|^no\b/.test(v)) return 'danger';
  if (/parcial|atenci|condic|medio|media|ámbar|ambar|amarill|revisar|ajustad|limitad|riesgo/.test(v)) return 'warning';
  return 'success';
}

function numOr(v) { const n = Number(v); return Number.isFinite(n) ? n : null; }

function SignalDot({ signal, size = 10 }) {
  const c = (SIGNAL[signal] || SIGNAL.neutral).color;
  return <span style={{width:size, height:size, borderRadius:99, background:c, boxShadow:`0 0 0 3px color-mix(in srgb, ${c} 22%, transparent)`, flexShrink:0, display:'inline-block'}}/>;
}

function GoQuadrant({ icon, title, signal = 'neutral', metrics = [], items = [], empty }) {
  const Ico = Icon[icon] || Icon.Info;
  const sig = SIGNAL[signal] || SIGNAL.neutral;
  return (
    <div className="card card-pad" style={{display:'flex', flexDirection:'column', gap:12, borderTop:`3px solid ${sig.color}`, minWidth:0}}>
      <div style={{display:'flex', alignItems:'center', gap:8}}>
        <Ico size={15} style={{color:'var(--text-3)'}}/>
        <span style={{fontSize:13.5, fontWeight:600, flex:1}}>{title}</span>
        <span style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:11, color:sig.color, fontWeight:500}}><SignalDot signal={signal} size={8}/> {sig.label}</span>
      </div>
      {metrics.length > 0 && (
        <div style={{display:'grid', gridTemplateColumns:`repeat(${Math.min(metrics.length, 3)}, minmax(0,1fr))`, gap:10}}>
          {metrics.map((m, i) => (
            <div key={i} style={{minWidth:0}}>
              <div style={{fontSize:10.5, color:'var(--text-4)', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{m.label}</div>
              <div className="font-mono" style={{fontSize:16, marginTop:2, color: m.signal ? (SIGNAL[m.signal] || SIGNAL.neutral).color : 'var(--text-1)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}} title={String(m.value)}>{m.value}</div>
            </div>
          ))}
        </div>
      )}
      {items.length > 0 ? (
        <ul style={{margin:0, padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:6}}>
          {items.slice(0, 4).map((it, i) => (
            <li key={i} style={{display:'flex', gap:8, alignItems:'flex-start', fontSize:12.5, lineHeight:1.45, color:'var(--text-2)'}}>
              <span style={{marginTop:5}}><SignalDot signal={it.signal || 'neutral'} size={6}/></span>
              <span style={{display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}} title={it.text}>{it.text}</span>
            </li>
          ))}
        </ul>
      ) : (empty && metrics.length === 0 ? <div style={{fontSize:12.5, color:'var(--text-4)'}}>{empty}</div> : null)}
    </div>
  );
}

function GoDetails({ title, meta, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card" style={{overflow:'hidden'}}>
      <button type="button" onClick={()=>setOpen((o)=>!o)} style={{width:'100%', display:'flex', alignItems:'center', gap:8, padding:'12px 16px', background:'transparent', border:0, cursor:'pointer', color:'var(--text-1)', textAlign:'left'}}>
        {open ? <Icon.ChevronDown size={14}/> : <Icon.ChevronRight size={14}/>}
        <span style={{fontSize:13, fontWeight:500, flex:1}}>{title}</span>
        {meta}
      </button>
      {open && <div style={{padding:'0 16px 16px'}}>{children}</div>}
    </div>
  );
}

function TabGo({ bid, a }) {
  const initial = (bid && bid.go_data) || {};
  const [manual, setManual] = useState(initial.manualCriteria || {});
  const [competition, setCompetition] = useState(nonEmptyArr(initial.competition) ? initial.competition : []);
  const [decision, setDecision] = useState(initial.decision || autoGoState(a));
  const [note, setNote] = useState(initial.decisionNote || '');
  const [saving, setSaving] = useState(false);

  const snap = a.snapshot || {};
  const crit = a.criteria || {};
  const auto = crit.automatic || [];
  const subj = crit.subjective || [];
  const sol = a.solvency || {};
  const eco = a.economic || {};
  const inv = a.invoicing || {};
  const team = a.team || {};
  const riskItems = (a.risks || {}).items || [];
  const verdict = (a.viability || {}).verdict || [];

  const setCrit = (name, field, value) => {
    setManual((m) => ({ ...m, [name]: { ...(m[name] || {}), [field]: value } }));
  };

  const totals = auto.reduce((acc, c) => {
    const max = Number(c.max) || 0;
    const mv = manual[c.name] || {};
    const score = mv.ourScore !== undefined && mv.ourScore !== '' ? Number(mv.ourScore) : (Number(c.estimated) || 0);
    acc.max += max; acc.score += (Number.isFinite(score) ? score : 0);
    return acc;
  }, { max: 0, score: 0 });
  const subjTotals = subj.reduce((acc, c) => {
    acc.max += Number(c.max) || 0; acc.score += Number(c.estimated) || 0; return acc;
  }, { max: 0, score: 0 });

  const addCompRow = () => setCompetition((c) => [...c, { edition:'', company:'', awarded:false, score:'', offer:'', notes:'' }]);
  const setComp = (i, field, value) => setCompetition((c) => c.map((r, j) => j === i ? { ...r, [field]: value } : r));
  const delComp = (i) => setCompetition((c) => c.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      await updateGoData(bid.id, { manualCriteria: manual, competition, decision, decisionNote: note });
      notify('Análisis del Go guardado.', 'CheckCircle');
    } catch (e) {
      notify('No se pudo guardar: ' + (e?.message || 'error'), 'AlertTriangle');
    } finally { setSaving(false); }
  };

  // ----- Señales por cuadrante -----
  const verdictFor = (re) => verdict.filter((v) => re.test(String(v.dimension || '').toLowerCase()));

  // 1) Economía
  const budget = numOr(eco.budgetBase) ?? numOr(bid?.budget_base);
  const cost = numOr(eco.estimatedCost);
  const margin = numOr(eco.marginPct) ?? (budget && cost != null ? ((budget - cost) / budget) * 100 : null);
  const marginSig = margin == null ? 'neutral' : margin >= 15 ? 'success' : margin >= 5 ? 'warning' : 'danger';
  const finSig = inv.needsFinancing === true ? 'warning' : inv.needsFinancing === false ? 'success' : 'neutral';
  const ecoVerdict = verdictFor(/econ|margen|financ|precio|coste/).map((v) => signalFromText(v.status));

  // 2) Solvencia y equipo
  const solvItems = [...(sol.economic || []), ...(sol.technical || [])].filter(Boolean);
  const profiles = team.profiles || [];
  const solVerdict = verdictFor(/solven|equipo|perfil|capacidad|recurs|técn|tecn|operat/);
  const solSig = solVerdict.length ? worstSignal(solVerdict.map((v) => signalFromText(v.status))) : (solvItems.length ? 'warning' : 'neutral');

  // 3) Puntuación
  const allMax = totals.max + subjTotals.max;
  const allScore = totals.score + subjTotals.score;
  const scorePct = allMax > 0 ? (allScore / allMax) * 100 : null;
  const scoreSig = scorePct == null ? 'neutral' : scorePct >= 70 ? 'success' : scorePct >= 55 ? 'warning' : 'danger';

  // 4) Riesgos y plazo
  const days = bid?.deadline ? Math.ceil((new Date(bid.deadline).getTime() - Date.now()) / 86_400_000) : null;
  const daysSig = days == null ? 'neutral' : days < 0 ? 'danger' : days < 7 ? 'danger' : days < 15 ? 'warning' : 'success';
  const riskSig = (r) => {
    const p = String(r.probability || '').toLowerCase(); const im = String(r.impact || '').toLowerCase();
    if (p === 'alta' && im === 'alto') return 'danger';
    if (p === 'alta' || im === 'alto') return 'warning';
    return 'success';
  };
  const sortedRisks = [...riskItems].sort((x, y) => SIGNAL_RANK[riskSig(y)] - SIGNAL_RANK[riskSig(x)]);
  const highRisks = riskItems.filter((r) => riskSig(r) !== 'success').length;
  const riskSigAll = worstSignal([daysSig, ...(riskItems.length ? [highRisks >= 3 ? 'danger' : highRisks > 0 ? 'warning' : 'success'] : [])]);

  const quadrants = [
    {
      icon:'Coins', title:'Economía', signal: worstSignal([marginSig, finSig, ...ecoVerdict]),
      metrics: [
        { label:'Presupuesto', value: budget != null ? fmtMoney(budget) : '—' },
        { label:'Coste estimado', value: cost != null ? fmtMoney(cost) : '—' },
        { label:'Margen', value: margin != null ? fmtPct(Math.round(margin * 10) / 10) : '—', signal: marginSig },
      ],
      items: [
        inv.needsFinancing != null ? { text: inv.needsFinancing ? `Requiere financiación${hasText(inv.financingReason) ? ': ' + inv.financingReason : ''}` : 'No requiere financiación', signal: finSig } : null,
        hasText(eco.abnormalLow) ? { text: `Baja anormal: ${eco.abnormalLow}`, signal:'warning' } : null,
      ].filter(Boolean),
    },
    {
      icon:'Shield', title:'Solvencia y equipo', signal: solSig,
      metrics: [
        { label:'Requisitos solvencia', value: solvItems.length || '—' },
        { label:'Perfiles exigidos', value: profiles.length || '—' },
      ],
      items: [
        ...solVerdict.map((v) => ({ text: `${v.dimension}: ${v.status}${v.comment ? ' — ' + v.comment : ''}`, signal: signalFromText(v.status) })),
        ...solvItems.slice(0, 3).map((t) => ({ text: t, signal:'neutral' })),
      ],
      empty: 'Sin requisitos de solvencia detectados.',
    },
    {
      icon:'Target', title:'Puntuación esperada', signal: scoreSig,
      metrics: [
        { label:'Total estimado', value: allMax > 0 ? `${allScore.toFixed(1)} / ${allMax.toFixed(0)}` : '—', signal: scoreSig },
        { label:'Automáticos', value: totals.max > 0 ? `${totals.score.toFixed(1)} / ${totals.max.toFixed(0)}` : '—' },
        { label:'Juicio de valor', value: subjTotals.max > 0 ? `${subjTotals.score.toFixed(1)} / ${subjTotals.max.toFixed(0)}` : '—' },
      ],
      items: hasText(crit.distribution) ? [{ text: crit.distribution, signal:'neutral' }] : [],
    },
    {
      icon:'AlertTriangle', title:'Riesgos y plazo', signal: riskSigAll,
      metrics: [
        { label:'Cierre', value: days == null ? '—' : days < 0 ? 'Vencido' : `${days} días`, signal: daysSig },
        { label:'Riesgos altos', value: riskItems.length ? `${highRisks} / ${riskItems.length}` : '—', signal: riskItems.length ? (highRisks >= 3 ? 'danger' : highRisks > 0 ? 'warning' : 'success') : undefined },
      ],
      items: sortedRisks.slice(0, 3).map((r) => ({ text: r.risk, signal: riskSig(r) })),
      empty: 'Sin riesgos identificados.',
    },
  ];

  const st = GO_STATES[decision] || GO_STATES.pending;
  const StIco = Icon[st.icon] || Icon.Info;
  const rec = REC[snap.recommendation];
  const strengths = (snap.strengths || []).filter(Boolean);
  const attention = (snap.attention || []).filter(Boolean);

  return (
    <div className="stagger" style={{display:'flex', flexDirection:'column', gap:16}}>
      {/* Veredicto + decisión */}
      <div className="card card-pad" style={{display:'grid', gridTemplateColumns:'minmax(0,1fr) auto', gap:16, alignItems:'center'}}>
        <div style={{display:'flex', alignItems:'center', gap:14, minWidth:0}}>
          <span className={`sem ${st.tone}`} style={{width:48, height:48, borderRadius:12, justifyContent:'center', padding:0, flexShrink:0}}><StIco size={24}/></span>
          <div style={{minWidth:0}}>
            <div style={{fontSize:11, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em'}}>Decisión</div>
            <div className="font-display" style={{fontSize:24, lineHeight:1.15}}>{st.label}</div>
            {rec && (
              <div style={{fontSize:12, color:'var(--text-3)', marginTop:3}}>
                Lizzi recomienda: <span style={{color:'var(--text-1)'}}>{rec.label}</span>{snap.confidence != null ? ` · confianza ${snap.confidence}%` : ''}
              </div>
            )}
          </div>
        </div>
        <div style={{display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end'}}>
          {Object.keys(GO_STATES).map((k) => (
            <button key={k} className={`btn ${decision === k ? 'btn-primary' : 'btn-ghost'}`} style={{fontSize:12}} onClick={()=>setDecision(k)}>
              {GO_STATES[k].label}
            </button>
          ))}
        </div>
      </div>

      {/* Cuadrante de decisión */}
      <div className="go-quad">
        {quadrants.map((q) => <GoQuadrant key={q.title} {...q}/>)}
      </div>

      {/* A favor / En contra */}
      {(strengths.length > 0 || attention.length > 0) && (
        <div className="go-quad">
          <GoQuadrant icon="CheckCircle" title="A favor" signal={strengths.length ? 'success' : 'neutral'} items={strengths.map((t) => ({ text:t, signal:'success' }))} empty="—"/>
          <GoQuadrant icon="AlertCircle" title="En contra / a vigilar" signal={attention.length ? 'warning' : 'neutral'} items={attention.map((t) => ({ text:t, signal:'warning' }))} empty="—"/>
        </div>
      )}

      {/* Justificación + guardar */}
      <div className="card card-pad" style={{display:'flex', flexDirection:'column', gap:10}}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:10}}>
          <div className="section-title" style={{margin:0}}>Justificación de la decisión</div>
          <button className="btn btn-primary" disabled={saving} onClick={save}><Icon.Save size={14}/> {saving ? 'Guardando…' : 'Guardar análisis del Go'}</button>
        </div>
        <textarea style={{...GO_INPUT_STYLE, minHeight:64, resize:'vertical'}} value={note} placeholder={hasText(snap.rationale) ? snap.rationale : 'Argumentos clave del Go / No Go…'} onChange={(e)=>setNote(e.target.value)}/>
      </div>

      {/* Detalle editable (plegado) */}
      <GoDetails title="Criterios automáticos · valores propios" meta={<span className="chip"><Icon.Target size={11}/> {totals.score.toFixed(1)} / {totals.max.toFixed(0)} pts</span>}>
        {nonEmptyArr(auto) ? (
          <div className="card" style={{overflow:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Criterio</th><th>Máx.</th><th>Fórmula</th><th>Nuestro valor</th><th>Puntos est.</th></tr></thead>
              <tbody>
                {auto.map((c, i) => {
                  const mv = manual[c.name] || {};
                  return (
                    <tr key={i}>
                      <td style={{fontSize:12.5}}>{c.name || '—'}</td>
                      <td style={{fontSize:12.5}}>{c.max ?? '—'}</td>
                      <td style={{fontSize:12, color:'var(--text-3)'}}>{c.formula || '—'}</td>
                      <td><input style={GO_INPUT_STYLE} value={mv.ourValue || ''} placeholder="p. ej. 12,5%" onChange={(e)=>setCrit(c.name, 'ourValue', e.target.value)}/></td>
                      <td><input style={{...GO_INPUT_STYLE, width:90}} type="number" value={mv.ourScore ?? ''} placeholder={c.estimated ?? ''} onChange={(e)=>setCrit(c.name, 'ourScore', e.target.value)}/></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <Para text="No se han detectado criterios automáticos en el análisis."/>}
      </GoDetails>

      <GoDetails title="Competencia · ediciones anteriores" meta={<span className="chip">{competition.length}</span>}>
        <div style={{display:'flex', justifyContent:'flex-end', marginBottom:8}}>
          <button className="btn btn-ghost" style={{fontSize:12}} onClick={addCompRow}><Icon.Plus size={13}/> Añadir fila</button>
        </div>
        <div className="card" style={{overflow:'auto'}}>
          <table className="tbl">
            <thead><tr><th>Edición/Tramo</th><th>Empresa</th><th>Adjudicataria</th><th>Puntuación</th><th>Oferta económica</th><th>Notas</th><th></th></tr></thead>
            <tbody>
              {competition.length === 0 && (
                <tr><td colSpan={7} style={{fontSize:12.5, color:'var(--text-4)', textAlign:'center', padding:14}}>Sin datos de competencia. Añade filas o re-analiza para que Lizzi los proponga.</td></tr>
              )}
              {competition.map((r, i) => (
                <tr key={i}>
                  <td><input style={GO_INPUT_STYLE} value={r.edition || ''} onChange={(e)=>setComp(i,'edition',e.target.value)}/></td>
                  <td><input style={GO_INPUT_STYLE} value={r.company || ''} onChange={(e)=>setComp(i,'company',e.target.value)}/></td>
                  <td style={{textAlign:'center'}}><input type="checkbox" checked={!!r.awarded} onChange={(e)=>setComp(i,'awarded',e.target.checked)}/></td>
                  <td><input style={{...GO_INPUT_STYLE, width:80}} value={r.score || ''} onChange={(e)=>setComp(i,'score',e.target.value)}/></td>
                  <td><input style={GO_INPUT_STYLE} value={r.offer || ''} onChange={(e)=>setComp(i,'offer',e.target.value)}/></td>
                  <td><input style={GO_INPUT_STYLE} value={r.notes || ''} onChange={(e)=>setComp(i,'notes',e.target.value)}/></td>
                  <td><button className="btn btn-ghost" style={{padding:6}} onClick={()=>delComp(i)}><Icon.Trash size={13}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GoDetails>
    </div>
  );
}

// ---------- Tab: Criterios (calculadora de puntuación) ----------
function critNum(v) {
  if (v === null || v === undefined || v === '') return NaN;
  const n = Number(String(v).replace(/\./g, '').replace(',', '.').replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : NaN;
}

const PRICE_METHODS = [
  { id:'manual', label:'Manual / fórmula del pliego' },
  { id:'inversa', label:'Proporcional inversa (mejor oferta / nuestra oferta)' },
  { id:'baja_mejor', label:'Baja sobre la mejor oferta' },
  { id:'baja_lineal', label:'Proporcional a la baja máxima' },
];

function isPriceCriterion(c) {
  const t = `${c.name || ''} ${c.formula || ''}`.toLowerCase();
  return /precio|oferta econ|econ[oó]mic|baja/.test(t);
}

// Devuelve { pts, reason, baja }. pts=NaN cuando faltan datos; reason explica qué falta.
function computePriceResult(price) {
  const max = critNum(price.max) || 0;
  const base = critNum(price.base);
  const offer = critNum(price.offer);
  const best = critNum(price.bestOffer);
  const clamp = (v) => Math.max(0, Math.min(max, v));
  const bajaPct = (Number.isFinite(base) && base > 0 && Number.isFinite(offer)) ? ((base - offer) / base) * 100 : NaN;

  switch (price.method) {
    case 'inversa': {
      if (!Number.isFinite(offer) || offer <= 0) return { pts: NaN, reason: 'Introduce nuestra oferta (€).', baja: bajaPct };
      if (!Number.isFinite(best) || best <= 0) return { pts: NaN, reason: 'Introduce la mejor oferta esperada (€) para calcular con la fórmula inversa.', baja: bajaPct };
      return { pts: clamp(max * (best / offer)), reason: '', baja: bajaPct };
    }
    case 'baja_mejor': {
      if (!Number.isFinite(base) || base <= 0) return { pts: NaN, reason: 'Introduce el presupuesto base (€).', baja: bajaPct };
      if (!Number.isFinite(offer)) return { pts: NaN, reason: 'Introduce nuestra oferta (€).', baja: bajaPct };
      if (!Number.isFinite(best) || best <= 0) return { pts: NaN, reason: 'Introduce la mejor oferta esperada (€) como referencia.', baja: bajaPct };
      const bajaOur = Math.max(0, (base - offer) / base);
      const bajaRef = (base - best) / base;
      if (bajaRef <= 0) return { pts: bajaOur <= 0 ? 0 : max, reason: '', baja: bajaPct };
      return { pts: clamp(max * (bajaOur / bajaRef)), reason: '', baja: bajaPct };
    }
    case 'baja_lineal': {
      if (!Number.isFinite(base) || base <= 0) return { pts: NaN, reason: 'Introduce el presupuesto base (€).', baja: bajaPct };
      if (!Number.isFinite(offer)) return { pts: NaN, reason: 'Introduce nuestra oferta (€).', baja: bajaPct };
      const bajaMaxPct = critNum(price.maxBajaPct);
      if (!Number.isFinite(bajaMaxPct) || bajaMaxPct <= 0) return { pts: NaN, reason: 'Introduce la baja máxima de referencia (%).', baja: bajaPct };
      const bajaOur = Math.max(0, (base - offer) / base);
      return { pts: clamp(max * (bajaOur / (bajaMaxPct / 100))), reason: '', baja: bajaPct };
    }
    default: {
      const m = critNum(price.points);
      if (!Number.isFinite(m)) return { pts: NaN, reason: 'Introduce los puntos manualmente o elige un método de cálculo.', baja: bajaPct };
      return { pts: clamp(m), reason: '', baja: bajaPct };
    }
  }
}

// Compat: usos que sólo quieren los puntos numéricos.
function computePricePoints(price) {
  return computePriceResult(price).pts;
}


function TabCriteria({ crit, eco, bid }) {
  const saved = (bid && bid.go_data && bid.go_data.criteriaCalc) || {};
  const autoSrc = (crit.automatic || []);
  const priceFromAnalysis = autoSrc.find(isPriceCriterion);
  const nonPriceAnalysis = autoSrc.filter(c => !isPriceCriterion(c));

  const [autoRows, setAutoRows] = useState(
    nonEmptyArr(saved.auto) ? saved.auto :
    nonPriceAnalysis.map(c => ({ name:c.name || '', max: c.max ?? '', ourValue:'', ourScore: c.estimated ?? '' }))
  );
  const [subjRows, setSubjRows] = useState(
    nonEmptyArr(saved.subjective) ? saved.subjective :
    (crit.subjective || []).map(c => ({ name:c.name || '', max:c.max ?? '', howToScore:c.howToScore || '', recommendation:c.recommendation || '', ourScore: c.estimated ?? '' }))
  );
  const [price, setPrice] = useState(saved.price || {
    max: priceFromAnalysis?.max ?? '',
    base: eco.budgetBase ?? '',
    offer: '', bestOffer: '', maxBajaPct: '', method: 'manual', points: '',
    formula: priceFromAnalysis?.formula || '',
  });
  const [scenarios, setScenarios] = useState(nonEmptyArr(saved.scenarios) ? saved.scenarios : []);
  const [saving, setSaving] = useState(false);

  const setAuto = (i, f, v) => setAutoRows(rs => rs.map((r,j)=> j===i ? { ...r, [f]:v } : r));
  const setSubj = (i, f, v) => setSubjRows(rs => rs.map((r,j)=> j===i ? { ...r, [f]:v } : r));
  const setP = (f, v) => setPrice(p => ({ ...p, [f]:v }));

  const priceResult = computePriceResult(price);
  const pricePts = priceResult.pts;
  const priceMax = critNum(price.max) || 0;

  const totals = (() => {
    let max = 0, score = 0;
    autoRows.forEach(r => { max += critNum(r.max)||0; score += critNum(r.ourScore)||0; });
    subjRows.forEach(r => { max += critNum(r.max)||0; score += critNum(r.ourScore)||0; });
    max += priceMax; score += (Number.isFinite(pricePts) ? pricePts : 0);
    return { max, score, lost: Math.max(0, max - score) };
  })();

  const save = async () => {
    setSaving(true);
    try {
      await updateGoData(bid.id, { criteriaCalc: { auto: autoRows, subjective: subjRows, price, scenarios } });
      notify('Calculadora de criterios guardada.', 'CheckCircle');
    } catch (e) {
      notify('No se pudo guardar: ' + (e?.message || 'error'), 'AlertTriangle');
    } finally { setSaving(false); }
  };

  const addAuto = () => setAutoRows(rs => [...rs, { name:'', max:'', ourValue:'', ourScore:'' }]);
  const delAuto = (i) => setAutoRows(rs => rs.filter((_,j)=>j!==i));
  const addScen = () => setScenarios(s => [...s, { offer:'', points:'', margin:'', note:'' }]);
  const setScen = (i,f,v) => setScenarios(s => s.map((r,j)=> j===i ? { ...r,[f]:v } : r));
  const delScen = (i) => setScenarios(s => s.filter((_,j)=>j!==i));

  return (
    <div className="stagger" style={{display:'flex', flexDirection:'column', gap:22}}>
      {hasText(crit.distribution) && <Para text={crit.distribution}/>}

      {/* Resumen total */}
      <div className="card card-pad" style={{display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:16,
        background:'linear-gradient(135deg, var(--bg-1), var(--bg-2))'}}>
        <div style={{display:'flex', alignItems:'center', gap:14}}>
          <span className="sem sem-info" style={{width:44, height:44, borderRadius:12, justifyContent:'center', padding:0}}><Icon.Target size={22}/></span>
          <div>
            <div style={{fontSize:11.5, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em'}}>Puntuación total estimada</div>
            <div className="font-display" style={{fontSize:24, lineHeight:1.2, marginTop:2}}>
              {totals.score.toFixed(1)} <span style={{color:'var(--text-3)', fontSize:16}}>/ {totals.max.toFixed(0)} pts</span>
            </div>
          </div>
        </div>
        <div style={{textAlign:'right'}}>
          <div className="font-display-heavy" style={{fontSize:22, lineHeight:1, color:'var(--text-1)'}}>−{totals.lost.toFixed(1)}</div>
          <div style={{fontSize:11, color:'var(--text-3)'}}>puntos no alcanzables</div>
        </div>
      </div>

      {/* Criterios automáticos (no precio) */}
      <div>
        <div className="section-title" style={{marginBottom:8, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <span>Criterios automáticos (Sobre 3)</span>
          <button className="btn btn-ghost" style={{fontSize:12}} onClick={addAuto}><Icon.Plus size={13}/> Añadir criterio</button>
        </div>
        <div className="card" style={{overflow:'auto'}}>
          <table className="tbl">
            <thead><tr><th>Criterio</th><th>Máx.</th><th>Valor previsto</th><th>Puntos est.</th><th>No alcanz.</th><th></th></tr></thead>
            <tbody>
              {autoRows.length === 0 && <tr><td colSpan={6} style={{fontSize:12.5, color:'var(--text-4)', textAlign:'center', padding:14}}>Sin criterios automáticos. Añade filas manualmente.</td></tr>}
              {autoRows.map((r,i) => {
                const max = critNum(r.max)||0; const sc = critNum(r.ourScore)||0; const lost = Math.max(0, max-sc);
                return (
                  <tr key={i}>
                    <td><input style={GO_INPUT_STYLE} value={r.name} onChange={e=>setAuto(i,'name',e.target.value)}/></td>
                    <td><input style={{...GO_INPUT_STYLE, width:70}} value={r.max} onChange={e=>setAuto(i,'max',e.target.value)}/></td>
                    <td><input style={GO_INPUT_STYLE} value={r.ourValue} placeholder="p. ej. 12,5%" onChange={e=>setAuto(i,'ourValue',e.target.value)}/></td>
                    <td><input style={{...GO_INPUT_STYLE, width:80}} value={r.ourScore} onChange={e=>setAuto(i,'ourScore',e.target.value)}/></td>
                    <td style={{fontSize:12.5, color: lost>0?'var(--text-3)':'var(--text-4)'}}>{lost>0?`−${lost.toFixed(1)}`:'—'}</td>
                    <td><button className="btn btn-ghost" style={{padding:6}} onClick={()=>delAuto(i)}><Icon.Trash size={13}/></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Criterio precio (simulador) */}
      <div className="card card-pad">
        <div className="section-title" style={{marginBottom:12, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <span>Criterio precio · simulador</span>
          <span className="chip"><Icon.Coins size={11}/> {Number.isFinite(pricePts) ? pricePts.toFixed(1) : '—'} / {priceMax || '—'} pts</span>
        </div>
        {hasText(price.formula) && <div className="callout callout-info" style={{marginBottom:12}}><div className="callout-icon"><Icon.Info size={14}/></div><div style={{fontSize:12.5, lineHeight:1.5}}>Fórmula del pliego: {price.formula}</div></div>}
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px,1fr))', gap:12}}>
          <Field label="Método de cálculo">
            <select style={GO_INPUT_STYLE} value={price.method} onChange={e=>setP('method', e.target.value)}>
              {PRICE_METHODS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </Field>
          <Field label="Máx. puntos"><input style={GO_INPUT_STYLE} value={price.max} onChange={e=>setP('max',e.target.value)}/></Field>
          <Field label="Presupuesto base (€)"><input style={GO_INPUT_STYLE} value={price.base} onChange={e=>setP('base',e.target.value)}/></Field>
          <Field label="Nuestra oferta (€)"><input style={GO_INPUT_STYLE} value={price.offer} onChange={e=>setP('offer',e.target.value)}/></Field>
          {(price.method==='inversa'||price.method==='baja_mejor') && (
            <Field label="Mejor oferta esperada (€)"><input style={GO_INPUT_STYLE} value={price.bestOffer} onChange={e=>setP('bestOffer',e.target.value)}/></Field>
          )}
          {price.method==='baja_lineal' && (
            <Field label="Baja máxima de referencia (%)"><input style={GO_INPUT_STYLE} value={price.maxBajaPct} onChange={e=>setP('maxBajaPct',e.target.value)}/></Field>
          )}
          {price.method==='manual' && (
            <Field label="Puntos (manual)"><input style={GO_INPUT_STYLE} value={price.points} onChange={e=>setP('points',e.target.value)}/></Field>
          )}
        </div>
        {/* Resultado paso a paso / aviso de datos faltantes */}
        {priceResult.reason ? (
          <div className="callout callout-warn" style={{marginTop:12}}>
            <div className="callout-icon"><Icon.AlertTriangle size={14}/></div>
            <div style={{fontSize:12.5, lineHeight:1.5}}>{priceResult.reason}</div>
          </div>
        ) : (
          <div style={{marginTop:12, display:'flex', flexWrap:'wrap', gap:16, padding:'12px 14px', borderRadius:10, background:'var(--bg-2)', border:'1px solid var(--line-1)'}}>
            <div><div style={{fontSize:10.5, color:'var(--text-4)', textTransform:'uppercase', letterSpacing:'0.04em'}}>Baja aplicada</div><div style={{fontSize:14, fontWeight:600, marginTop:2}}>{Number.isFinite(priceResult.baja) ? `${priceResult.baja.toFixed(1)}%` : '—'}</div></div>
            <div><div style={{fontSize:10.5, color:'var(--text-4)', textTransform:'uppercase', letterSpacing:'0.04em'}}>Puntos de precio</div><div style={{fontSize:14, fontWeight:600, marginTop:2, color:'var(--primary)'}}>{Number.isFinite(pricePts) ? `${pricePts.toFixed(1)} / ${priceMax || '—'}` : '—'}</div></div>
          </div>
        )}
      </div>


      {/* Simulación de ofertas */}
      <div>
        <div className="section-title" style={{marginBottom:8, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <span>Simulación de ofertas económicas</span>
          <button className="btn btn-ghost" style={{fontSize:12}} onClick={addScen}><Icon.Plus size={13}/> Añadir escenario</button>
        </div>
        <div className="card" style={{overflow:'auto'}}>
          <table className="tbl">
            <thead><tr><th>Oferta (€)</th><th>Puntos precio</th><th>Margen</th><th>Nota</th><th></th></tr></thead>
            <tbody>
              {scenarios.length === 0 && <tr><td colSpan={5} style={{fontSize:12.5, color:'var(--text-4)', textAlign:'center', padding:14}}>Sin escenarios. Añade ofertas para comparar la puntuación de precio.</td></tr>}
              {scenarios.map((r,i) => {
                const simPts = computePricePoints({ ...price, offer: r.offer });
                return (
                  <tr key={i}>
                    <td><input style={GO_INPUT_STYLE} value={r.offer} onChange={e=>setScen(i,'offer',e.target.value)}/></td>
                    <td style={{fontSize:12.5, fontWeight:600}}>{Number.isFinite(simPts) ? simPts.toFixed(1) : (r.points || '—')}</td>
                    <td><input style={GO_INPUT_STYLE} value={r.margin} onChange={e=>setScen(i,'margin',e.target.value)}/></td>
                    <td><input style={GO_INPUT_STYLE} value={r.note} onChange={e=>setScen(i,'note',e.target.value)}/></td>
                    <td><button className="btn btn-ghost" style={{padding:6}} onClick={()=>delScen(i)}><Icon.Trash size={13}/></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Criterios subjetivos (Sobre 2) */}
      {nonEmptyArr(subjRows) && (
        <div>
          <div className="section-title" style={{marginBottom:8}}>Criterios sujetos a juicio de valor (Sobre 2)</div>
          <div className="card" style={{overflow:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Criterio</th><th>Máx.</th><th>Cómo puntuar</th><th>Recomendación</th><th>Puntos previstos</th></tr></thead>
              <tbody>
                {subjRows.map((r,i) => (
                  <tr key={i}>
                    <td style={{fontSize:12.5}}>{r.name || '—'}</td>
                    <td style={{fontSize:12.5}}>{r.max ?? '—'}</td>
                    <td style={{fontSize:12, color:'var(--text-3)'}}>{r.howToScore || '—'}</td>
                    <td style={{fontSize:12, color:'var(--text-3)'}}>{r.recommendation || '—'}</td>
                    <td><input style={{...GO_INPUT_STYLE, width:90}} value={r.ourScore} onChange={e=>setSubj(i,'ourScore',e.target.value)}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {nonEmptyArr(crit.roi) && <div><div className="section-title" style={{marginBottom:8}}>ROI de mejoras</div><MiniTable headers={['Mejora','Coste','Puntos','ROI']} rows={crit.roi.map(x=>[x.improvement,x.cost,x.points,x.roi])}/></div>}

      <div style={{display:'flex', justifyContent:'flex-end'}}>
        <button className="btn btn-primary" disabled={saving} onClick={save}><Icon.Save size={14}/> {saving ? 'Guardando…' : 'Guardar calculadora'}</button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{fontSize:10.5, color:'var(--text-4)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:5}}>{label}</div>
      {children}
    </div>
  );
}

// ---------- Tab: Equipo ----------
function ProfileCard({ p }) {
  const [open, setOpen] = useState(false);
  const head = [p.people || p.count, p.dedication].filter(Boolean).join(' · ');
  const rows = [
    ['Nº de personas', p.people || p.count],
    ['Dedicación', p.dedication],
    ['Nº de horas', p.hours],
    ['% de jornada', p.journeyPct],
    ['Presencialidad', p.onsite],
    ['Lugar de prestación', p.location],
    ['Titulación requerida', p.qualification],
    ['Experiencia requerida', p.experience],
    ['Coste/h máx.', p.maxRate],
    ['Requisito', p.requirement],
  ].filter(([, v]) => v && v !== 'Dato no encontrado en pliegos');
  return (
    <div className="card">
      <button onClick={()=>setOpen(o=>!o)} style={{width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, background:'none', border:'none', cursor:'pointer', padding:'14px 16px', color:'var(--text-1)', textAlign:'left'}}>
        <div style={{display:'flex', alignItems:'center', gap:12, minWidth:0}}>
          <span className="sem sem-violet" style={{width:34, height:34, borderRadius:9, justifyContent:'center', padding:0, flexShrink:0}}><Icon.Users size={16}/></span>
          <div style={{minWidth:0}}>
            <div style={{fontWeight:600, fontSize:13.5}}>{p.role || 'Perfil'}</div>
            {head && <div style={{fontSize:12, color:'var(--text-3)', marginTop:2}}>{head}</div>}
          </div>
        </div>
        <Icon.ChevronDown size={16} style={{transform: open?'rotate(180deg)':'none', transition:'transform .2s', flexShrink:0, color:'var(--text-3)'}}/>
      </button>
      {open && (
        <div style={{padding:'0 16px 16px'}}>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px,1fr))', gap:12}}>
            {rows.map(([k,v]) => (
              <div key={k}><div style={{fontSize:10.5, color:'var(--text-4)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:3}}>{k}</div><div style={{fontSize:13, color:'var(--text-2)'}}>{v}</div></div>
            ))}
          </div>
          {p.functions && p.functions !== 'Dato no encontrado en pliegos' && (
            <div style={{marginTop:12}}>
              <div style={{fontSize:10.5, color:'var(--text-4)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:3}}>Descripción de funciones</div>
              <Para text={p.functions}/>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TabTeam({ team }) {
  return (
    <div className="stagger" style={{display:'flex', flexDirection:'column', gap:22}}>
      {hasText(team.summary) && <Para text={team.summary}/>}
      {nonEmptyArr(team.profiles) && (
        <div>
          <div className="section-title" style={{marginBottom:8}}>Perfiles requeridos · {team.profiles.length}</div>
          <div style={{display:'flex', flexDirection:'column', gap:10}}>
            {team.profiles.map((p,i) => <ProfileCard key={i} p={p}/>)}
          </div>
        </div>
      )}
      {nonEmptyArr(team.coverage) && <div><div className="section-title" style={{marginBottom:8}}>Matriz de cobertura</div><MiniTable headers={['Perfil','Asignadas','Necesarias','Cobertura','Riesgo']} rows={team.coverage.map(x=>[x.profile,x.assigned,x.needed,x.coverage,x.risk])}/></div>}
      {hasText(team.scalability) && <Para text={team.scalability}/>}
    </div>
  );
}

// ---------- Tab: Presentación de ofertas ----------
function TabSubmission({ sub, docs }) {
  const envelopes = nonEmptyArr(sub.envelopes) ? sub.envelopes : null;
  const contamination = nonEmptyArr(sub.contamination) ? sub.contamination : (hasText(docs.contaminationWarning) ? [docs.contaminationWarning] : []);
  const fallback = !envelopes ? [
    { number:'Sobre 1', name:'Documentación administrativa', docs: docs.envelope1 },
    { number:'Sobre 2', name:'Documentación técnica', docs: docs.envelope2 },
    { number:'Sobre 3', name:'Documentación económica', docs: docs.envelope3 },
  ].filter(e => nonEmptyArr(e.docs)) : null;

  return (
    <div className="stagger" style={{display:'flex', flexDirection:'column', gap:18}}>
      <Para text="Cómo debe presentarse la oferta: sobres/archivos, documentación con sus anexos y reglas de contaminación entre sobres."/>

      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px,1fr))', gap:14}}>
        {envelopes && envelopes.map((e,i) => (
          <div key={i} className="card card-pad">
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:10}}>
              <span className="sem sem-info" style={{width:30, height:30, borderRadius:8, justifyContent:'center', padding:0}}><Icon.FileText size={15}/></span>
              <div>
                <div style={{fontSize:11, color:'var(--text-4)', textTransform:'uppercase', letterSpacing:'0.04em'}}>{e.number || `Sobre ${i+1}`}</div>
                <div style={{fontWeight:600, fontSize:13.5}}>{e.name || '—'}</div>
              </div>
            </div>
            {nonEmptyArr(e.documents) ? (
              <ul style={{margin:0, paddingLeft:0, listStyle:'none', display:'flex', flexDirection:'column', gap:7}}>
                {e.documents.map((d,j) => (
                  <li key={j} style={{fontSize:12.5, color:'var(--text-2)', lineHeight:1.45, display:'flex', gap:7}}>
                    <Icon.CheckCircle size={13} style={{flexShrink:0, marginTop:2, color:'var(--text-4)'}}/>
                    <span>{d.name || '—'}{d.annex ? <span style={{color:'var(--text-4)'}}> · {d.annex}</span> : null}</span>
                  </li>
                ))}
              </ul>
            ) : <Para text="Sin documentos detallados."/>}
          </div>
        ))}
        {fallback && fallback.map((e,i) => (
          <div key={i} className="card card-pad">
            <div style={{fontWeight:600, fontSize:13, marginBottom:8}}>{e.number} · {e.name}</div>
            <Bullets items={e.docs}/>
          </div>
        ))}
      </div>

      {nonEmptyArr(contamination) && (
        <div>
          <div className="section-title" style={{marginBottom:8, display:'flex', alignItems:'center', gap:8}}><Icon.AlertTriangle size={14}/> Contaminación entre sobres</div>
          <div className="callout callout-warn" style={{flexDirection:'column', alignItems:'flex-start'}}>
            <ul style={{margin:0, paddingLeft:18, display:'flex', flexDirection:'column', gap:6}}>
              {contamination.map((t,i) => <li key={i} style={{fontSize:12.5, lineHeight:1.5}}>{t}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Tab: ANS y compromisos ----------
function TabAns({ commitments, solvency }) {
  const c = commitments || {}; const sv = solvency || {};
  return (
    <div className="stagger" style={{display:'flex', flexDirection:'column', gap:22}}>
      {hasText(c.warning) && <div className="callout callout-warn"><div className="callout-icon"><Icon.AlertTriangle size={15}/></div><div style={{fontSize:12.5, lineHeight:1.5}}>{c.warning}</div></div>}
      {nonEmptyArr(c.items) && <div><div className="section-title" style={{marginBottom:8}}>Compromisos cuantificables</div><MiniTable headers={['Compromiso','Mínimo','Recomendación','Máximo','Puntos','Riesgo']} rows={c.items.map(x=>[x.commitment,x.min,x.recommendation,x.max,x.points,x.risk])}/></div>}
      {nonEmptyArr(sv.specialConditions) && <div><div className="section-title" style={{marginBottom:8}}>Condiciones especiales de ejecución</div><MiniTable headers={['Condición','Nivel','Penalización','Esencial','Implicación']} rows={sv.specialConditions.map(x=>[x.title,x.level,x.penalty,x.essential?'Sí':'No',x.implication])}/></div>}
    </div>
  );
}

// ---------- Tab: Riesgos ----------
function TabRisks({ risks }) {
  const items = (risks.items || []).map((x) => {
    const score = (['baja','media','alta'].indexOf((x.probability||'').toLowerCase())+1) * (['bajo','medio','alto'].indexOf((x.impact||'').toLowerCase())+1);
    return {
      name: x.risk, prob: (x.probability||'media').toLowerCase(), impact: (x.impact||'medio').toLowerCase(),
      mitigation: x.mitigation || '—', level: score >= 6 ? 'high' : score >= 3 ? 'medium' : 'low',
    };
  });
  return (
    <div className="stagger" style={{display:'flex', flexDirection:'column', gap:18}}>
      <div style={{display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:18}}>
        <div>
          <div className="section-title" style={{marginBottom:10}}>Riesgos identificados · {items.length}</div>
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            {items.map((r,i) => <RiskRow key={i} risk={r}/>)}
          </div>
        </div>
        <div>
          <div className="section-title" style={{marginBottom:10}}>Risk radar · probabilidad × impacto</div>
          <RiskRadar risks={items}/>
          <div style={{display:'flex', gap:8, marginTop:14, fontSize:11.5, color:'var(--text-3)'}}>
            <span className="sem sem-danger">Alto</span>
            <span className="sem sem-warning">Medio</span>
            <span className="sem sem-success">Bajo</span>
          </div>
        </div>
      </div>
      {hasText(risks.maxExposure) && <div className="callout callout-warn"><div className="callout-icon"><Icon.AlertTriangle size={15}/></div><div style={{fontSize:12.5, lineHeight:1.5}}>Exposición máxima teórica: <strong>{risks.maxExposure}</strong></div></div>}
    </div>
  );
}

// ---------- Tab: Entregables ----------
function TabDeliverables({ deliver, docs }) {
  return (
    <div className="stagger" style={{display:'flex', flexDirection:'column', gap:22}}>
      {nonEmptyArr(deliver.items) && <div><div className="section-title" style={{marginBottom:8}}>Entregables y periodicidad</div><MiniTable headers={['Código','Entregable','Periodicidad','Responsable']} rows={deliver.items.map(x=>[x.code,x.name,x.freq,x.owner])}/></div>}
      {hasText(deliver.note) && <Para text={deliver.note}/>}
      {nonEmptyArr(docs.criticalDates) && <div><div className="section-title" style={{marginBottom:8}}>Fechas críticas</div><MiniTable headers={['Hito','Plazo','Implicación']} rows={docs.criticalDates.map(x=>[x.milestone,x.date,x.implication])}/></div>}
    </div>
  );
}

// ---------- Barra de pestañas con flechas de desplazamiento ----------
function TabNav({ tabDefs, tab, setTab }) {
  const ref = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return;
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [update, tabDefs.length]);

  // Desplaza la pestaña activa a la vista al cambiar.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const active = el.querySelector('.tabnav-item.active');
    if (active) active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [tab]);

  const scrollBy = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * Math.max(220, el.clientWidth * 0.7), behavior: 'smooth' });
  };

  return (
    <div className="tabnav-wrap">
      <button className="tabnav-arrow" onClick={()=>scrollBy(-1)} disabled={!canLeft} aria-label="Anteriores"><Icon.ChevronLeft size={16}/></button>
      <div className="tabnav" role="tablist" ref={ref}>
        {tabDefs.map(t => {
          const TIco = Icon[t.icon] || Icon.Dot;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab===t.id}
              className={`tabnav-item ${tab===t.id?'active':''}`}
              onClick={()=>setTab(t.id)}
            >
              <TIco size={15}/>
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>
      <button className="tabnav-arrow" onClick={()=>scrollBy(1)} disabled={!canRight} aria-label="Siguientes"><Icon.ChevronRight size={16}/></button>
    </div>
  );
}

// ---------- Tab: Documentos ----------

function fmtFileSize(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function BidFilesSection({ bid }) {
  const [files, setFiles] = useState(null);
  const [opening, setOpening] = useState(null);
  useEffect(() => {
    if (!bid?.id) { setFiles([]); return; }
    let active = true;
    listBidDocuments(bid.id)
      .then((d) => { if (active) setFiles(d); })
      .catch(() => { if (active) setFiles([]); });
    return () => { active = false; };
  }, [bid?.id]);

  const open = async (doc) => {
    setOpening(doc.id);
    try {
      const url = await getBidDocumentUrl(doc.id);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      notify('No se pudo abrir el archivo: ' + (e?.message || 'error'), 'AlertTriangle');
    } finally { setOpening(null); }
  };

  if (files === null) return null;

  return (
    <div>
      <div className="section-title" style={{marginBottom:8}}>Archivos del expediente · {files.length}</div>
      {files.length === 0 ? (
        <div className="card card-pad" style={{fontSize:12.5, color:'var(--text-4)'}}>No hay archivos subidos para esta licitación.</div>
      ) : (
        <div className="card" style={{overflow:'hidden'}}>
          {files.map((f, i) => (
            <div key={f.id} style={{display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderTop: i ? '1px solid var(--line-1)' : 'none'}}>
              <span className="sem sem-info" style={{width:34, height:34, borderRadius:9, justifyContent:'center', padding:0, flexShrink:0}}><Icon.FileText size={16}/></span>
              <div style={{minWidth:0, flex:1}}>
                <div style={{fontWeight:600, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{f.file_name}</div>
                <div style={{fontSize:11.5, color:'var(--text-4)', marginTop:2}}>
                  {fmtFileSize(f.size)}
                  {f.extract_status && f.extract_status !== 'done' ? ` · texto ${f.extract_status}` : ''}
                </div>
              </div>
              <button className="btn btn-outline" style={{fontSize:12}} disabled={opening===f.id} onClick={()=>open(f)}>
                <Icon.ArrowUpRight size={13}/> {opening===f.id ? 'Abriendo…' : 'Abrir'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TabDocuments({ docs, bid }) {
  return (
    <div className="stagger" style={{display:'flex', flexDirection:'column', gap:18}}>
      {hasText(docs.contaminationWarning) && <div className="callout callout-warn"><div className="callout-icon"><Icon.AlertTriangle size={15}/></div><div style={{fontSize:12.5, lineHeight:1.5}}>{docs.contaminationWarning}</div></div>}
      <BidFilesSection bid={bid}/>
      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px,1fr))', gap:14}}>
        {nonEmptyArr(docs.envelope1) && <div className="card card-pad"><div style={{fontWeight:600, fontSize:13, marginBottom:8}}>Sobre 1 · Administrativo</div><Bullets items={docs.envelope1}/></div>}
        {nonEmptyArr(docs.envelope2) && <div className="card card-pad"><div style={{fontWeight:600, fontSize:13, marginBottom:8}}>Sobre 2 · Técnico</div><Bullets items={docs.envelope2}/></div>}
        {nonEmptyArr(docs.envelope3) && <div className="card card-pad"><div style={{fontWeight:600, fontSize:13, marginBottom:8}}>Sobre 3 · Económico</div><Bullets items={docs.envelope3}/></div>}
      </div>
    </div>
  );
}


// ---------- Informe completo (11 secciones, render lineal) ----------
function IILFullReport({ bid, a }) {
  const s = a.snapshot || {};
  return (
    <div className="stagger">
      {hasText(a.executiveReport) && (
        <div className="card card-pad" style={{marginBottom:6, background:'linear-gradient(135deg, var(--bg-1), var(--bg-2))'}}>
          <div className="section-title" style={{marginBottom:8}}>Resumen ejecutivo</div>
          <MarkdownLite text={a.executiveReport}/>
        </div>
      )}
      {a.snapshot && (
        <IILSection n={1} title="Snapshot decisional">
          <Para text={s.rationale}/>
        </IILSection>
      )}
      {a.general && (
        <IILSection n={2} title="Información general">
          <MiniTable headers={['Campo','Valor']} rows={(a.general.fields||[]).map(f=>[f.label,f.value])}/>
          <Para text={a.general.lots}/>
          <Para text={a.general.contractImplication}/>
        </IILSection>
      )}
      {a.economic && (
        <IILSection n={3} title="Análisis económico">
          <MiniTable headers={['Concepto','Importe','%']} rows={(a.economic.breakdown||[]).map(b=>[b.concept,b.amount,b.pct])}/>
          <MiniTable headers={['Cálculo','Fórmula','Resultado']} rows={(a.economic.calculations||[]).map(c=>[c.name,c.formula,c.result])}/>
          <Para text={a.economic.abnormalLow}/>
          <Para text={a.economic.strategy}/>
        </IILSection>
      )}
      {a.solvency && (
        <IILSection n={4} title="Requisitos de solvencia">
          {a.solvency.economic?.length > 0 && <><div style={{fontSize:12.5, fontWeight:600, marginTop:6}}>Económica</div><Bullets items={a.solvency.economic}/></>}
          {a.solvency.technical?.length > 0 && <><div style={{fontSize:12.5, fontWeight:600, marginTop:10}}>Técnica</div><Bullets items={a.solvency.technical}/></>}
          <MiniTable headers={['Perfil','Requisito']} rows={(a.solvency.professional||[]).map(x=>[x.role,x.requirement])}/>
          <MiniTable headers={['Condición','Nivel','Penalización','Esencial','Implicación']} rows={(a.solvency.specialConditions||[]).map(x=>[x.title,x.level,x.penalty,x.essential?'Sí':'No',x.implication])}/>
        </IILSection>
      )}
      {a.service && (
        <IILSection n={5} title="Descripción del servicio">
          <MiniTable headers={['Actividad','Categoría','Horas','Entregables']} rows={(a.service.activities||[]).map(x=>[x.name,x.category,x.hours,x.deliverables])}/>
          <Para text={a.service.workload}/>
          <Para text={a.service.hiddenLoad}/>
        </IILSection>
      )}
      {a.team && (
        <IILSection n={6} title="Equipo requerido">
          <Para text={a.team.summary}/>
          <MiniTable headers={['Perfil','Nº','Requisito','Dedicación','Coste/h máx.']} rows={(a.team.profiles||[]).map(x=>[x.role,x.count,x.requirement,x.dedication,x.maxRate])}/>
          <MiniTable headers={['Perfil','Asignadas','Necesarias','Cobertura','Riesgo']} rows={(a.team.coverage||[]).map(x=>[x.profile,x.assigned,x.needed,x.coverage,x.risk])}/>
          <Para text={a.team.scalability}/>
        </IILSection>
      )}
      {a.criteria && (
        <IILSection n={7} title="Criterios de valoración">
          <Para text={a.criteria.distribution}/>
          <MiniTable headers={['Criterio (Sobre 2)','Puntos','Cómo puntuar','Recomendación']} rows={(a.criteria.subjective||[]).map(x=>[x.name,x.points,x.howToScore,x.recommendation])}/>
          <MiniTable headers={['Criterio (Sobre 3)','Puntos','Fórmula','Recomendación']} rows={(a.criteria.automatic||[]).map(x=>[x.name,x.points,x.formula,x.recommendation])}/>
          <MiniTable headers={['Escenario','Puntos','Margen','Nota']} rows={(a.criteria.priceScenarios||[]).map(x=>[x.scenario,x.points,x.margin,x.note])}/>
          <MiniTable headers={['Mejora','Coste','Puntos','ROI']} rows={(a.criteria.roi||[]).map(x=>[x.improvement,x.cost,x.points,x.roi])}/>
        </IILSection>
      )}
      {a.documents && (
        <IILSection n={8} title="Documentación a presentar">
          <Para text={a.documents.contaminationWarning}/>
          {a.documents.envelope1?.length > 0 && <><div style={{fontSize:12.5, fontWeight:600, marginTop:8}}>Sobre 1</div><Bullets items={a.documents.envelope1}/></>}
          {a.documents.envelope2?.length > 0 && <><div style={{fontSize:12.5, fontWeight:600, marginTop:10}}>Sobre 2</div><Bullets items={a.documents.envelope2}/></>}
          {a.documents.envelope3?.length > 0 && <><div style={{fontSize:12.5, fontWeight:600, marginTop:10}}>Sobre 3</div><Bullets items={a.documents.envelope3}/></>}
          <MiniTable headers={['Hito','Plazo','Implicación']} rows={(a.documents.criticalDates||[]).map(x=>[x.milestone,x.date,x.implication])}/>
        </IILSection>
      )}
      {a.commitments && (
        <IILSection n={9} title="Compromisos cuantificables">
          <Para text={a.commitments.warning}/>
          <MiniTable headers={['Compromiso','Mínimo','Recomendación','Máximo','Puntos','Riesgo']} rows={(a.commitments.items||[]).map(x=>[x.commitment,x.min,x.recommendation,x.max,x.points,x.risk])}/>
        </IILSection>
      )}
      {a.risks && (
        <IILSection n={10} title="Análisis de riesgos">
          <MiniTable headers={['Riesgo','Probabilidad','Impacto','Importe','Mitigación']} rows={(a.risks.items||[]).map(x=>[x.risk,x.probability,x.impact,x.impactValue,x.mitigation])}/>
          {a.risks.maxExposure && <Para text={`Exposición máxima teórica: ${a.risks.maxExposure}`}/>}
        </IILSection>
      )}
      {a.viability && (
        <IILSection n={11} title="Viabilidad operativa">
          <Para text={a.viability.equation}/>
          <MiniTable headers={['Bloque','Necesarias','Disponibles','Cobertura']} rows={(a.viability.coverage||[]).map(x=>[x.block,x.needed,x.available,x.coverage])}/>
          <Para text={a.viability.bottlenecks}/>
          <MiniTable headers={['Dimensión','Estado','Comentario']} rows={(a.viability.verdict||[]).map(x=>[x.dimension,x.status,x.comment])}/>
        </IILSection>
      )}
    </div>
  );
}

window.BidDetailScreen = BidDetailScreen;


export { BidDetailScreen, SandetelHeader, SandetelOverview, BigKpi, ConditionCard, SandetelBudget, BudgetRow, SandetelCriteria, CriteriaTable, ScoreBarChart, SandetelTeam, SandetelANS, SandetelRisks, RiskRow, RiskRadar, SandetelDeliverables, SandetelDocuments, BidDetailGeneric, TabNav };
